<script setup>
import { ref, computed, onMounted, getCurrentInstance, nextTick, watch } from 'vue';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import { Head, usePage } from '@inertiajs/inertia-vue3';
import { Icon } from '@iconify/vue';
import PremiumPageHeader from "@/Components/PremiumPageHeader.vue";
import ModalAlert from '@/Components/ModalAlert.vue';
import BarChart from "@/Components/Charts/BarChart.vue";
import LineChart from "@/Components/Charts/LineChart.vue";
import RadarChartExample from "@/Components/Charts/RadarChartExample.vue";
import axios from 'axios';
import * as XLSX from 'xlsx';

// Props received from controller
const props = defineProps({
    arrayBimestre: Object,
    parentLevels: Array,
    childLevels: Array,
    tiposExamen: Array,
    areasCursos: Array,
    studentsList: Array,
    simulacros: Array,
    dashboardTop3: Object,
    stats: Object,
    isAdmin: Boolean,
    isCoord: Boolean
});

// Reactive state
const instance = getCurrentInstance();
const confGlobal = instance.appContext.config.globalProperties.confGlobal;
const activeTab = ref('resumen'); // resumen, simulacros, configuracion
const currentUserId = computed(() => usePage().props.value.auth.user.id);

// --- TABS & GENERAL DATA STATE ---
const localTiposExamen = ref([...props.tiposExamen]);
const localSimulacros = ref([...props.simulacros]);
const localChildLevels = ref([...props.childLevels]);

// --- CONFIGURACIÓN TAB STATE ---
const newTipo = ref({ label: '', factor_positivo: 20.0, factor_negativo: -1.25 });
const newTipoFile = ref(null);
const editingTipo = ref(null);
const newCurso = ref({ id_tipo_examen: '', id_area: '', label: '', cantidad_preguntas: 10 });
const levelConfigEditing = ref(null);

// --- SIMULACROS TAB STATE ---
const newSimulacro = ref({ id_tipo_examen: '', label: '', ref_ciclo: '', id_level_parent: '' });
const selectedSimulacro = ref(null);
const resultados = ref([]);
const loadingResultados = ref(false);
const savingRowId = ref(null);
const studentFilter = ref('');
const gradeFilter = ref('');
const studentToAdd = ref('');
const searchStudentQuery = ref('');
const showStudentDropdown = ref(false);
const isAddingStudent = ref(false);
const isBulkAdding = ref(false);

const filteredStudentsList = computed(() => {
    if (!selectedSimulacro.value) return [];
    
    // Find child level IDs for the selected parent level
    const childIds = props.childLevels
        .filter(cl => cl.id_parent === selectedSimulacro.value.id_level_parent)
        .map(cl => cl.id);
        
    // Filter students: must belong to one of the child level IDs
    let list = props.studentsList;
    if (selectedSimulacro.value.id_level_parent) {
        list = list.filter(s => childIds.includes(s.id_level));
    }
    
    // Filter out students who are already registered in the simulacro
    const registeredStudentIds = resultados.value.map(r => r.id_student);
    return list.filter(s => !registeredStudentIds.includes(s.id));
});

const filteredAddableStudents = computed(() => {
    const term = searchStudentQuery.value.trim().toLowerCase();
    if (!term) return filteredStudentsList.value.slice(0, 50); // limit to 50 for performance
    return filteredStudentsList.value.filter(s => 
        s.fullname.toLowerCase().includes(term) || 
        s.level_label.toLowerCase().includes(term)
    );
});

const selectStudentFromDropdown = (student) => {
    studentToAdd.value = student.id;
    searchStudentQuery.value = student.fullname;
    showStudentDropdown.value = false;
};

const handleStudentInputBlur = () => {
    setTimeout(() => {
        showStudentDropdown.value = false;
    }, 200);
};

// Sync Scrollbars & Table width calculation
const scrollContainer = ref(null);
const topScrollContainer = ref(null);
const tableElement = ref(null);
const tableWidth = ref(0);

const syncScrollTop = () => {
    if (topScrollContainer.value && scrollContainer.value) {
        scrollContainer.value.scrollLeft = topScrollContainer.value.scrollLeft;
    }
};

const syncScrollBottom = () => {
    if (topScrollContainer.value && scrollContainer.value) {
        topScrollContainer.value.scrollLeft = scrollContainer.value.scrollLeft;
    }
};

const updateTableWidth = () => {
    if (tableElement.value) {
        tableWidth.value = tableElement.value.scrollWidth;
    }
};

watch([resultados, selectedSimulacro], () => {
    nextTick(() => {
        updateTableWidth();
    });
}, { deep: true });

// Arrow key / Enter navigation for fast manual grading inputs
const handleKeyNavigation = (event, rIdx, cIdx, type) => {
    const key = event.key;
    const coursesCount = currentExamType.value ? currentExamType.value.cursos.length : 0;
    const resultsCount = filteredResultados.value.length;

    let targetR = rIdx;
    let targetC = cIdx;
    let targetType = type;

    if (key === 'ArrowRight' || key === 'Enter') {
        event.preventDefault();
        if (type === 'buenas') {
            targetType = 'malas';
        } else {
            if (cIdx + 1 < coursesCount) {
                targetC = cIdx + 1;
                targetType = 'buenas';
            } else if (rIdx + 1 < resultsCount) {
                targetR = rIdx + 1;
                targetC = 0;
                targetType = 'buenas';
            }
        }
    } else if (key === 'ArrowLeft') {
        event.preventDefault();
        if (type === 'malas') {
            targetType = 'buenas';
        } else {
            if (cIdx > 0) {
                targetC = cIdx - 1;
                targetType = 'malas';
            } else if (rIdx > 0) {
                targetR = rIdx - 1;
                targetC = coursesCount - 1;
                targetType = 'malas';
            }
        }
    } else if (key === 'ArrowDown') {
        event.preventDefault();
        if (rIdx + 1 < resultsCount) {
            targetR = rIdx + 1;
        }
    } else if (key === 'ArrowUp') {
        event.preventDefault();
        if (rIdx > 0) {
            targetR = rIdx - 1;
        }
    } else {
        return; // Let other keys behave normally
    }

    // Focus and highlight target input
    const targetId = `input-${targetType}-${targetR}-${targetC}`;
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
        targetElement.focus();
        targetElement.select(); // Highlight cell contents for fast entry
    }
};

const exportGridToExcel = () => {
    if (!selectedSimulacro.value) return;

    const examTitle = selectedSimulacro.value.label || 'Simulacro';
    const examTypeLabel = currentExamType.value ? currentExamType.value.label : '';
    const courses = currentExamType.value ? currentExamType.value.cursos : [];

    // Title rows
    const data = [
        [examTitle.toUpperCase()],
        [`Tipo de Examen: ${examTypeLabel}`],
        [], // Empty separator
    ];

    // Build header row 1 (Main headers)
    const headerRow1 = ['Estudiante', 'Grado'];
    courses.forEach(curso => {
        headerRow1.push(curso.label, '', '');
    });
    headerRow1.push('Resumen Buenas', 'Resumen Malas', 'Puntaje Total', 'Estado');

    // Build header row 2 (Sub-headers)
    const headerRow2 = ['', ''];
    courses.forEach(() => {
        headerRow2.push('Buenas (B)', 'Malas (M)', 'Eficiencia (%)');
    });
    headerRow2.push('', '', '', '');

    data.push(headerRow1);
    data.push(headerRow2);

    // Map rows (filtered results)
    filteredResultados.value.forEach(row => {
        const rowData = [
            row.fullname,
            row.level_label
        ];

        courses.forEach(curso => {
            const detail = row.detalles_editables[curso.id] || {};
            rowData.push(
                detail.buenas !== undefined ? detail.buenas : 0,
                detail.malas !== undefined ? detail.malas : 0,
                detail.eficiencia !== undefined ? `${detail.eficiencia}%` : '0%'
            );
        });

        rowData.push(
            row.total_buenas,
            row.total_malas,
            row.puntaje_total,
            row.resultado === 1 ? 'Ingresó' : 'No ingresó'
        );

        data.push(rowData);
    });

    // Generate sheet & workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);

    // Auto-calculate column widths
    const colWidths = [];
    data.forEach(row => {
        row.forEach((val, colIndex) => {
            const strVal = val ? val.toString() : '';
            colWidths[colIndex] = Math.max(colWidths[colIndex] || 8, strVal.length + 3);
        });
    });
    ws['!cols'] = colWidths.map(w => ({ wch: w }));

    // Define merges to make it look incredibly premium!
    const merges = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
        // Merge "Estudiante" vertically
        { s: { r: 3, c: 0 }, e: { r: 4, c: 0 } },
        // Merge "Grado" vertically
        { s: { r: 3, c: 1 }, e: { r: 4, c: 1 } }
    ];

    // Merge each course label horizontally across B, M, Ef.
    let colCursor = 2;
    courses.forEach(() => {
        merges.push({ s: { r: 3, c: colCursor }, e: { r: 3, c: colCursor + 2 } });
        colCursor += 3;
    });

    // Merge Resumen / Puntaje / Estado vertically
    merges.push(
        { s: { r: 3, c: colCursor }, e: { r: 4, c: colCursor } },      // Resumen Buenas
        { s: { r: 3, c: colCursor + 1 }, e: { r: 4, c: colCursor + 1 } },  // Resumen Malas
        { s: { r: 3, c: colCursor + 2 }, e: { r: 4, c: colCursor + 2 } },  // Puntaje Total
        { s: { r: 3, c: colCursor + 3 }, e: { r: 4, c: colCursor + 3 } }   // Estado
    );

    ws['!merges'] = merges;

    XLSX.utils.book_append_sheet(wb, ws, "Resultados");

    // Write file
    const sanitizedTitle = examTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    XLSX.writeFile(wb, `resultados_simulacro_${sanitizedTitle}.xlsx`);
    triggerToast("Excel generado y descargado correctamente");
};

const availableGrades = computed(() => {
    const grades = resultados.value.map(r => r.level_label).filter(Boolean);
    return [...new Set(grades)].sort();
});

// Filtered results
const filteredResultados = computed(() => {
    let list = resultados.value;
    if (gradeFilter.value) {
        list = list.filter(r => r.level_label === gradeFilter.value);
    }
    if (studentFilter.value) {
        const filter = studentFilter.value.toLowerCase();
        list = list.filter(r => r.fullname.toLowerCase().includes(filter));
    }
    return list;
});

// --- CUSTOM MODAL ALERT STATE ---
const alertState = ref({
    show: false,
    title: '',
    message: '',
    type: 'info',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
    showCancel: false,
    onConfirm: null,
    onCancel: null
});

const triggerAlert = (title, message, type = 'info', options = {}) => {
    return new Promise((resolve) => {
        alertState.value = {
            show: true,
            title,
            message,
            type,
            confirmText: options.confirmText || 'Confirmar',
            cancelText: options.cancelText || 'Cancelar',
            showCancel: options.showCancel || false,
            onConfirm: () => {
                alertState.value.show = false;
                resolve(true);
            },
            onCancel: () => {
                alertState.value.show = false;
                resolve(false);
            }
        };
    });
};

// --- CUSTOM TOAST STATE ---
const toastState = ref({
    show: false,
    message: ''
});

const triggerToast = (message) => {
    toastState.value.message = message;
    toastState.value.show = true;
    setTimeout(() => {
        toastState.value.show = false;
    }, 2000);
};



// Calculations on local results
const currentExamType = computed(() => {
    if (!selectedSimulacro.value) return null;
    return localTiposExamen.value.find(t => t.id === selectedSimulacro.value.id_tipo_examen) || null;
});

// Initial load
onMounted(() => {
    if (localSimulacros.value.length > 0) {
        selectSimulacro(localSimulacros.value[0]);
    }
});

// Select a simulacro to view/grade
const selectSimulacro = async (simulacro) => {
    selectedSimulacro.value = simulacro;
    loadingResultados.value = true;
    studentFilter.value = '';
    gradeFilter.value = '';
    try {
        const response = await axios.get(route('examenadmision-resultados'), {
            params: { id_simulacro: simulacro.id }
        });
        
        // Map details array to object keyed by course id for easy binding
        resultados.value = response.data.map(res => {
            const courseDetails = {};
            const courses = currentExamType.value ? currentExamType.value.cursos : [];
            courses.forEach(c => {
                const saved = res.detalles_cursos ? res.detalles_cursos[c.id] : null;
                courseDetails[c.id] = {
                    buenas: saved ? parseInt(saved.buenas || 0) : 0,
                    malas: saved ? parseInt(saved.malas || 0) : 0,
                    no_contesto: saved ? parseInt(saved.no_contesto || 0) : c.cantidad_preguntas,
                    eficiencia: saved ? parseFloat(saved.eficiencia || 0) : 0
                };
            });
            
            return {
                ...res,
                detalles_editables: courseDetails
            };
        });
    } catch (error) {
        console.error(error);
        triggerAlert('Error', 'No se pudieron cargar los resultados del simulacro', 'error');
    } finally {
        loadingResultados.value = false;
    }
};

// Add a single student
const addStudent = async () => {
    if (!studentToAdd.value || !selectedSimulacro.value) return;
    
    isAddingStudent.value = true;
    try {
        const response = await axios.post(route('examenadmision-resultados-add-student'), {
            id_simulacro: selectedSimulacro.value.id,
            id_student: studentToAdd.value
        });
        
        // Map details array to object keyed by course id for easy binding
        const res = response.data;
        const courseDetails = {};
        const courses = currentExamType.value ? currentExamType.value.cursos : [];
        courses.forEach(c => {
            const saved = res.detalles_cursos ? res.detalles_cursos[c.id] : null;
            courseDetails[c.id] = {
                buenas: saved ? parseInt(saved.buenas || 0) : 0,
                malas: saved ? parseInt(saved.malas || 0) : 0,
                no_contesto: saved ? parseInt(saved.no_contesto || 0) : c.cantidad_preguntas,
                eficiencia: saved ? parseFloat(saved.eficiencia || 0) : 0
            };
        });
        
        const mappedRes = {
            ...res,
            detalles_editables: courseDetails
        };
        
        resultados.value.push(mappedRes);
        resultados.value.sort((a, b) => a.fullname.localeCompare(b.fullname));
        
        studentToAdd.value = '';
        searchStudentQuery.value = '';
        triggerToast("Estudiante agregado correctamente.");
    } catch (error) {
        console.error(error);
        triggerAlert("Error", "No se pudo agregar el estudiante al simulacro.", "error");
    } finally {
        isAddingStudent.value = false;
    }
};

// Bulk add students
const bulkAddStudents = async () => {
    if (!selectedSimulacro.value) return;
    
    const confirm = await triggerAlert(
        "Confirmar Carga Masiva",
        "¿Está seguro de que desea agregar a todos los alumnos activos de este nivel al simulacro?",
        "warning",
        { showCancel: true, confirmText: "Sí, agregar", cancelText: "Cancelar" }
    );
    if (!confirm) return;
    
    isBulkAdding.value = true;
    try {
        const response = await axios.post(route('examenadmision-resultados-bulk-add'), {
            id_simulacro: selectedSimulacro.value.id
        });
        
        // Map details array to object keyed by course id for easy binding
        resultados.value = response.data.map(res => {
            const courseDetails = {};
            const courses = currentExamType.value ? currentExamType.value.cursos : [];
            courses.forEach(c => {
                const saved = res.detalles_cursos ? res.detalles_cursos[c.id] : null;
                courseDetails[c.id] = {
                    buenas: saved ? parseInt(saved.buenas || 0) : 0,
                    malas: saved ? parseInt(saved.malas || 0) : 0,
                    no_contesto: saved ? parseInt(saved.no_contesto || 0) : c.cantidad_preguntas,
                    eficiencia: saved ? parseFloat(saved.eficiencia || 0) : 0
                };
            });
            
            return {
                ...res,
                detalles_editables: courseDetails
            };
        });
        
        triggerToast("Se han cargado todos los alumnos al simulacro.");
    } catch (error) {
        console.error(error);
        triggerAlert("Error", "No se pudieron precargar los estudiantes.", "error");
    } finally {
        isBulkAdding.value = false;
    }
};

// Delete a student from the simulacro
const deleteRowResult = async (row) => {
    const confirm = await triggerAlert(
        "Eliminar Estudiante",
        `¿Está seguro de que desea retirar a ${row.fullname} de este simulacro? Se perderán las calificaciones ingresadas para este alumno en este simulacro.`,
        "warning",
        { showCancel: true, confirmText: "Sí, retirar", cancelText: "Cancelar" }
    );
    if (!confirm) return;
    
    try {
        await axios.delete(route('examenadmision-resultados-delete', { id: row.id }));
        resultados.value = resultados.value.filter(r => r.id !== row.id);
        triggerToast("Estudiante retirado del simulacro.");
    } catch (error) {
        console.error(error);
        triggerAlert("Error", "No se pudo retirar al estudiante.", "error");
    }
};

// Auto calculate score dynamically on the frontend when inputs change
const calculateRowDynamic = (row, courseId, type) => {
    const courses = currentExamType.value ? currentExamType.value.cursos : [];
    const changedCourse = courses.find(c => c.id === courseId);
    if (!changedCourse) return;

    let det = row.detalles_editables[courseId];
    if (!det) return; // course entry missing for this student row — skip

    // Ensure numbers
    det.buenas = isNaN(parseInt(det.buenas)) ? 0 : Math.max(0, parseInt(det.buenas));
    det.malas = isNaN(parseInt(det.malas)) ? 0 : Math.max(0, parseInt(det.malas));

    // Validations
    if (det.buenas + det.malas > changedCourse.cantidad_preguntas) {
        if (type === 'buenas') {
            det.buenas = changedCourse.cantidad_preguntas - det.malas;
        } else {
            det.malas = changedCourse.cantidad_preguntas - det.buenas;
        }
    }

    det.no_contesto = changedCourse.cantidad_preguntas - (det.buenas + det.malas);
    det.eficiencia = changedCourse.cantidad_preguntas > 0 ? Math.round((det.buenas * 100) / changedCourse.cantidad_preguntas) : 0;

    // Recalculate Row Totals
    let totBuenas = 0;
    let totMalas = 0;
    let totNoContesto = 0;

    courses.forEach(c => {
        const d = row.detalles_editables[c.id];
        totBuenas += d.buenas || 0;
        totMalas += d.malas || 0;
        totNoContesto += d.no_contesto || 0;
    });

    row.total_buenas = totBuenas;
    row.total_malas = totMalas;
    row.total_no_contesto = totNoContesto;

    const factorPos = parseFloat(currentExamType.value.factor_positivo);
    const factorNeg = parseFloat(currentExamType.value.factor_negativo);

    row.puntaje_positivo = totBuenas * factorPos;
    row.puntaje_negativo = totMalas * factorNeg;
    row.puntaje_total = row.puntaje_positivo - Math.abs(row.puntaje_negativo);

    const minScore = parseFloat(row.simulacro_min_score) || 360;
    row.resultado = row.puntaje_total >= minScore ? 1 : 0;
};

// Save a student result row
const saveRowResult = async (row) => {
    savingRowId.value = row.id;
    try {
        const response = await axios.post(route('examenadmision-resultados-save'), {
            id: row.id,
            detalles_cursos: row.detalles_editables
        });
        
        // Update local row with backend processed data
        const updated = response.data;
        row.total_buenas = updated.total_buenas;
        row.total_malas = updated.total_malas;
        row.total_no_contesto = updated.total_no_contesto;
        row.puntaje_positivo = updated.puntaje_positivo;
        row.puntaje_negativo = updated.puntaje_negativo;
        row.puntaje_total = updated.puntaje_total;
        row.resultado = updated.resultado;
        
        triggerToast(`Puntaje de ${row.fullname} guardado`);
    } catch (error) {
        console.error(error);
        triggerAlert('Error', 'No se pudo guardar la calificación del alumno', 'error');
    } finally {
        savingRowId.value = null;
    }
};

// --- CRUD ACTIONS ---

// Crear Tipo Examen
const handleStoreTipo = async () => {
    if (!newTipo.value.label || newTipo.value.factor_positivo === '' || newTipo.value.factor_negativo === '') {
        return triggerAlert('Atención', 'Por favor complete todos los campos', 'warning');
    }
    
    const formData = new FormData();
    formData.append('label', newTipo.value.label);
    formData.append('factor_positivo', newTipo.value.factor_positivo);
    formData.append('factor_negativo', newTipo.value.factor_negativo);
    if (newTipoFile.value && newTipoFile.value.files[0]) {
        formData.append('template_pdf', newTipoFile.value.files[0]);
    }
    
    try {
        const response = await axios.post(route('examenadmision-tipos-store'), formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        localTiposExamen.value.push({ ...response.data, cursos: [] });
        newTipo.value = { label: '', factor_positivo: 20.0, factor_negativo: -1.25 };
        if (newTipoFile.value) newTipoFile.value.value = ''; // clear file input
        triggerAlert('¡Éxito!', 'Tipo de examen registrado', 'success');
    } catch (error) {
        triggerAlert('Error', 'No se pudo registrar el tipo de examen', 'error');
    }
};

// Editar Tipo Examen
const handleUpdateTipo = async (tipo) => {
    if (!tipo.label || tipo.factor_positivo === '' || tipo.factor_negativo === '') {
        return triggerAlert('Atención', 'Por favor complete todos los campos', 'warning');
    }
    
    const formData = new FormData();
    formData.append('_method', 'PUT');
    formData.append('label', tipo.label);
    formData.append('factor_positivo', tipo.factor_positivo);
    formData.append('factor_negativo', tipo.factor_negativo);
    
    const fileInput = document.getElementById(`update-pdf-file-${tipo.id}`);
    if (fileInput && fileInput.files[0]) {
        formData.append('template_pdf', fileInput.files[0]);
    }
    
    try {
        const response = await axios.post(route('examenadmision-tipos-update', tipo.id), formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        // Update local object with new pdf path
        tipo.template_pdf_path = response.data.template_pdf_path;
        editingTipo.value = null;
        triggerAlert('¡Éxito!', 'Tipo de examen actualizado', 'success');
    } catch (error) {
        triggerAlert('Error', 'No se pudo actualizar el tipo de examen', 'error');
    }
};

// Eliminar Tipo Examen
const handleDeleteTipo = async (id) => {
    const result = await triggerAlert(
        '¿Estás seguro?',
        'Se eliminarán los cursos y simulacros asociados.',
        'warning',
        { showCancel: true, confirmText: 'Sí, eliminar', cancelText: 'Cancelar' }
    );
    
    if (result) {
        try {
            await axios.delete(route('examenadmision-tipos-delete', id));
            localTiposExamen.value = localTiposExamen.value.filter(t => t.id !== id);
            triggerAlert('Eliminado', 'Tipo de examen eliminado correctamente', 'success');
        } catch (error) {
            const msg = error.response?.data?.message || 'No se pudo eliminar el tipo de examen';
            triggerAlert('Atención', msg, 'warning');
        }
    }
};

// Crear Curso para un Tipo
const handleStoreCurso = async () => {
    if (!newCurso.value.id_tipo_examen || !newCurso.value.label || !newCurso.value.cantidad_preguntas) {
        return triggerAlert('Atención', 'Por favor complete todos los campos', 'warning');
    }
    try {
        const response = await axios.post(route('examenadmision-cursos-store'), newCurso.value);
        const tipo = localTiposExamen.value.find(t => t.id === newCurso.value.id_tipo_examen);
        if (tipo) {
            tipo.cursos.push(response.data);
        }
        newCurso.value = { id_tipo_examen: '', id_area: '', label: '', cantidad_preguntas: 10 };
        triggerAlert('¡Éxito!', 'Curso registrado correctamente', 'success');
    } catch (error) {
        triggerAlert('Error', 'No se pudo registrar el curso', 'error');
    }
};

// Eliminar Curso de un Tipo
const handleDeleteCurso = async (tipoId, cursoId) => {
    const result = await triggerAlert(
        '¿Estás seguro?',
        'Esta acción no se puede deshacer.',
        'warning',
        { showCancel: true, confirmText: 'Sí, eliminar', cancelText: 'Cancelar' }
    );
    
    if (result) {
        try {
            await axios.delete(route('examenadmision-cursos-delete', cursoId));
            const tipo = localTiposExamen.value.find(t => t.id === tipoId);
            if (tipo) {
                tipo.cursos = tipo.cursos.filter(c => c.id !== cursoId);
            }
            triggerAlert('Eliminado', 'Curso eliminado correctamente', 'success');
        } catch (error) {
            triggerAlert('Error', 'No se pudo eliminar el curso', 'error');
        }
    }
};

// Crear Simulacro
const handleStoreSimulacro = async () => {
    if (!newSimulacro.value.id_tipo_examen || !newSimulacro.value.label) {
        return triggerAlert('Atención', 'Por favor complete el nombre y tipo de examen', 'warning');
    }
    try {
        const response = await axios.post(route('examenadmision-simulacros-store'), newSimulacro.value);
        
        // Reload page to re-fetch full data including relations or append manually
        const created = response.data;
        const tipo = localTiposExamen.value.find(t => t.id === created.id_tipo_examen);
        const level = props.parentLevels.find(l => l.id === created.id_level_parent);
        
        const fullSimulacro = {
            ...created,
            tipo_examen: tipo,
            level_parent: level
        };
        
        localSimulacros.value.unshift(fullSimulacro);
        newSimulacro.value = { id_tipo_examen: '', label: '', ref_ciclo: '', id_level_parent: '' };
        
        triggerAlert('¡Éxito!', 'Simulacro creado correctamente', 'success');
        selectSimulacro(fullSimulacro);
    } catch (error) {
        triggerAlert('Error', 'No se pudo crear el simulacro', 'error');
    }
};

// Eliminar Simulacro
const handleDeleteSimulacro = async (id) => {
    const result = await triggerAlert(
        '¿Eliminar Simulacro?',
        'Se borrarán todos los resultados y calificaciones asociadas a este simulacro.',
        'warning',
        { showCancel: true, confirmText: 'Sí, eliminar', cancelText: 'Cancelar' }
    );
    
    if (result) {
        try {
            await axios.delete(route('examenadmision-simulacros-delete', id));
            localSimulacros.value = localSimulacros.value.filter(s => s.id !== id);
            if (selectedSimulacro.value?.id === id) {
                selectedSimulacro.value = localSimulacros.value[0] || null;
                if (selectedSimulacro.value) selectSimulacro(selectedSimulacro.value);
                else resultados.value = [];
            }
            triggerAlert('Eliminado', 'Simulacro eliminado correctamente', 'success');
        } catch (error) {
            const msg = error.response?.data?.message || 'No se pudo eliminar el simulacro';
            triggerAlert('Atención', msg, 'warning');
        }
    }
};

// --- CLAVE DE RESPUESTAS STATE & LOGIC ---
const showClaveModal = ref(false);
const savingClave = ref(false);
const claveQuestions = ref([]);

const openClaveModal = async () => {
    if (!selectedSimulacro.value) return;
    
    let questions = [];
    let currentNum = 1;
    
    if (currentExamType.value && currentExamType.value.cursos) {
        currentExamType.value.cursos.forEach(curso => {
            for (let i = 0; i < curso.cantidad_preguntas; i++) {
                questions.push({
                    numero_pregunta: currentNum,
                    id_curso: curso.id,
                    course_label: curso.label,
                    respuesta_correcta: null
                });
                currentNum++;
            }
        });
    }
    
    try {
        const response = await axios.get(route('examenadmision-respuestas-clave', selectedSimulacro.value.id));
        const savedAnswers = response.data.respuestas;
        
        savedAnswers.forEach(saved => {
            const q = questions.find(item => item.numero_pregunta === saved.numero_pregunta);
            if (q) {
                q.respuesta_correcta = saved.respuesta_correcta;
            }
        });
    } catch (error) {
        triggerAlert('Error', 'No se pudieron cargar las respuestas clave registradas.', 'error');
    }
    
    claveQuestions.value = questions;
    showClaveModal.value = true;
};

const saveClave = async () => {
    savingClave.value = true;
    try {
        await axios.post(route('examenadmision-respuestas-clave-save', selectedSimulacro.value.id), {
            respuestas: claveQuestions.value
        });
        showClaveModal.value = false;
        triggerAlert('¡Éxito!', 'Clave de respuestas guardada correctamente para este simulacro.', 'success');
    } catch (error) {
        triggerAlert('Error', 'No se pudo guardar la clave de respuestas.', 'error');
    } finally {
        savingClave.value = false;
    }
};

const groupCursosClave = computed(() => {
    const groups = {};
    claveQuestions.value.forEach(q => {
        if (!groups[q.course_label]) {
            groups[q.course_label] = [];
        }
        groups[q.course_label].push(q);
    });
    return groups;
});

// Guardar Configuración de Niveles (Grados)
const handleUpdateLevelConfig = async (lvl) => {
    if (lvl.simulacro_max_score === '' || lvl.simulacro_min_score === '') {
        return triggerAlert('Atención', 'Ingrese valores válidos', 'warning');
    }
    try {
        await axios.put(route('examenadmision-level-config', lvl.id), {
            simulacro_max_score: lvl.simulacro_max_score,
            simulacro_min_score: lvl.simulacro_min_score
        });

        // Update loaded results dynamically so they reflect the new config immediately
        resultados.value.forEach(row => {
            if (row.id_level === lvl.id) {
                row.simulacro_min_score = lvl.simulacro_min_score;
                row.simulacro_max_score = lvl.simulacro_max_score;
                
                const minScore = parseFloat(lvl.simulacro_min_score) || 360;
                row.resultado = row.puntaje_total >= minScore ? 1 : 0;
            }
        });

        levelConfigEditing.value = null;
        triggerAlert('¡Éxito!', 'Configuración de puntajes por nivel guardada', 'success');
    } catch (error) {
        triggerAlert('Error', 'No se pudo guardar la configuración del nivel', 'error');
    }
};

// --- REPORTES TAB STATE & LOGIC ---
const reportSubTab = ref('alumno'); // alumno, nivel
const selectedReportStudentId = ref('');
const reportData = ref(null);
const loadingReport = ref(false);

const selectedReportLevelId = ref('');
const selectedReportBimestre = ref('');
const showAllUsers = ref(false);
const levelReportData = ref([]);
const loadingLevelReport = ref(false);

const loadLevelReport = async () => {
    if (!selectedReportLevelId.value || !selectedReportBimestre.value) {
        levelReportData.value = [];
        return;
    }
    loadingLevelReport.value = true;
    try {
        const response = await axios.get(route('examenadmision-level-report'), {
            params: {
                id_level: selectedReportLevelId.value,
                ref_ciclo: selectedReportBimestre.value,
                show_all_users: showAllUsers.value
            }
        });
        levelReportData.value = response.data;
    } catch (error) {
        triggerAlert('Error', 'No se pudo cargar el reporte por nivel', 'error');
    } finally {
        loadingLevelReport.value = false;
    }
};

const printDiploma = (row) => {
    const url = route('examenadmision-diploma', { id: row.id, puesto: row.puesto });
    window.open(url, '_blank');
};

const downloadDiplomaPdf = (row) => {
    const url = route('examenadmision-diploma-pdf', { id: row.id, puesto: row.puesto });
    window.location.href = url;
};

const loadStudentReport = async () => {
    if (!selectedReportStudentId.value) {
        reportData.value = null;
        return;
    }
    loadingReport.value = true;
    try {
        const response = await axios.get(route('examenadmision-student-report'), {
            params: { id_student: selectedReportStudentId.value }
        });
        reportData.value = response.data;
    } catch (error) {
        triggerAlert('Error', 'No se pudo cargar el reporte del estudiante', 'error');
    } finally {
        loadingReport.value = false;
    }
};

const barChartData = computed(() => {
    if (!reportData.value || !reportData.value.resultados || !reportData.value.resultados.length) {
        return { labels: [], datasets: [] };
    }
    const labels = reportData.value.resultados.map(r => r.simulacro_label);
    const data = reportData.value.resultados.map(r => r.puntaje_total);
    return {
        labels,
        datasets: [
            {
                label: 'Puntaje Total',
                backgroundColor: '#6366f1',
                borderRadius: 8,
                data
            }
        ]
    };
});

const areaBarChartData = computed(() => {
    if (!reportData.value || !reportData.value.overall_efficiency) {
        return { labels: [], datasets: [] };
    }
    const labels = Object.keys(reportData.value.overall_efficiency);
    const data = Object.values(reportData.value.overall_efficiency);
    return {
        labels,
        datasets: [
            {
                label: 'Eficiencia (%)',
                backgroundColor: '#10b981',
                borderRadius: 8,
                data
            }
        ]
    };
});

const radarChartData = computed(() => {
    if (!reportData.value || !reportData.value.overall_efficiency) {
        return { labels: [], datasets: [] };
    }
    const labels = Object.keys(reportData.value.overall_efficiency);
    const data = Object.values(reportData.value.overall_efficiency);
    return {
        labels,
        datasets: [
            {
                label: 'Competencia (%)',
                borderColor: '#4f46e5',
                backgroundColor: 'rgba(79, 70, 229, 0.15)',
                pointBackgroundColor: '#4f46e5',
                borderWidth: 2,
                data
            }
        ]
    };
});

const lineChartData = computed(() => {
    if (!reportData.value || !reportData.value.resultados || !reportData.value.resultados.length) {
        return { labels: [], datasets: [] };
    }
    
    const labels = reportData.value.resultados.map(r => r.simulacro_label);
    
    const areaKeys = new Set();
    reportData.value.resultados.forEach(r => {
        if (r.efficiency_by_area) {
            Object.keys(r.efficiency_by_area).forEach(a => areaKeys.add(a));
        }
    });
    
    const colors = {
        'Matemática': { border: '#6366f1', bg: 'rgba(99, 102, 241, 0.15)' },
        'Letras': { border: '#ec4899', bg: 'rgba(236, 72, 153, 0.15)' },
        'Ciencias': { border: '#10b981', bg: 'rgba(16, 185, 129, 0.15)' },
        'Habilidad': { border: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' },
        'General': { border: '#64748b', bg: 'rgba(100, 116, 139, 0.15)' }
    };
    
    const datasets = Array.from(areaKeys).map(area => {
        const data = reportData.value.resultados.map(r => {
            return r.efficiency_by_area && r.efficiency_by_area[area] !== undefined 
                ? r.efficiency_by_area[area] 
                : 0;
        });
        
        const style = colors[area] || { border: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.15)' };
        
        return {
            label: area,
            data,
            borderColor: style.border,
            backgroundColor: style.bg,
            borderWidth: 3,
            pointBackgroundColor: style.border,
            pointRadius: 5,
            pointHoverRadius: 7,
            tension: 0.3,
            fill: false
        };
    });
    
    return {
        labels,
        datasets
    };
});

const getStudentName = (id) => {
    const student = props.studentsList?.find(s => s.id === parseInt(id));
    return student ? student.fullname : 'N/A';
};

const getStudentLevel = (id) => {
    const student = props.studentsList?.find(s => s.id === parseInt(id));
    return student ? student.level_label : 'N/A';
};

const printReport = () => {
    window.print();
};

const fileInputOMRFile = ref(null);
const fileInputOMRCamera = ref(null);
const currentOMRRow = ref(null);
const showOMRSelector = ref(false);

const triggerOMRUpload = (row) => {
    currentOMRRow.value = row;
    showOMRSelector.value = true;
};

const selectOMRCamera = () => {
    showOMRSelector.value = false;
    nextTick(() => {
        if (fileInputOMRCamera.value) {
            fileInputOMRCamera.value.click();
        }
    });
};

const selectOMRFile = () => {
    showOMRSelector.value = false;
    nextTick(() => {
        if (fileInputOMRFile.value) {
            fileInputOMRFile.value.click();
        }
    });
};

const handleOMRFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file || !currentOMRRow.value) return;
    
    triggerToast("Procesando imagen con OMR OpenCV...");
    
    const formData = new FormData();
    formData.append('sheet_image', file);
    formData.append('id_resultado', currentOMRRow.value.id);
    formData.append('id_simulacro', selectedSimulacro.value.id);
    
    try {
        const response = await axios.post(route('examenadmision-omr-process'), formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        
        if (response.data.success) {
            const detected = response.data.detalles_cursos;
            Object.keys(detected).forEach(courseId => {
                const cId = parseInt(courseId);
                if (currentOMRRow.value.detalles_editables[cId]) {
                    currentOMRRow.value.detalles_editables[cId].buenas = parseInt(detected[courseId].buenas);
                    currentOMRRow.value.detalles_editables[cId].malas = parseInt(detected[courseId].malas);
                    calculateRowDynamic(currentOMRRow.value, cId, 'buenas');
                }
            });
            
            const usedOpenCV = response.data.used_opencv;
            const message = response.data.message;

            if (!usedOpenCV && response.data.debug_info) {
                console.group('%c🔍 DIAGNÓSTICO OMR FALLBACK (DEPURACIÓN)', 'background: #1e1e38; color: #ff5e5e; font-weight: bold; padding: 4px 8px; border-radius: 6px;');
                console.warn("Comando Ejecutado:", response.data.debug_info.command);
                console.warn("Script Existe:", response.data.debug_info.script_exists ? "✅ Sí" : "❌ No");
                console.warn("Imagen Existe:", response.data.debug_info.image_exists ? "✅ Sí" : "❌ No");
                console.warn("Python Detectado por Apache/XAMPP:", response.data.debug_info.python_installed ? "✅ Sí" : "❌ No");
                console.error("Salida Detallada / Error de Python (stderr):", response.data.debug_info.raw_output);
                console.info("💡 Consejo: Abre la terminal en el servidor y ejecuta el comando anterior manualmente para aislar el error.");
                console.groupEnd();
            }

            // Print real-time side-by-side grading comparison in browser console
            if (response.data.respuestas_clave) {
                const keys = response.data.respuestas_clave;
                const detected = response.data.respuestas_detectadas || {};
                
                console.group('%c📊 COMPARATIVA DE RESPUESTAS (PROCESADOR OMR)', 'background: #0f172a; color: #38bdf8; font-weight: bold; padding: 6px 12px; border-radius: 6px;');
                console.info(`Estudiante: ${currentOMRRow.value.fullname}`);
                console.info(`Método Usado: ${usedOpenCV ? 'Visión Artificial (OpenCV)' : 'Generador/Clave Contigencia'}`);
                
                const tableData = [];
                Object.keys(keys).forEach(qNum => {
                    const studentMark = detected[qNum] || 'N';
                    const correctKey = keys[qNum];
                    let status = '⚪ Omitida';
                    if (studentMark !== 'N') {
                        status = (studentMark === correctKey) ? '✅ CORRECTA' : '❌ INCORRECTA';
                    }
                    tableData.push({
                        'Pregunta #': parseInt(qNum),
                        'Clave Oficial': correctKey,
                        'Marcado Alumno': studentMark,
                        'Resultado de Calificación': status
                    });
                });
                
                console.table(tableData);
                console.groupEnd();
            }

            triggerToast(usedOpenCV ? '¡Procesamiento OMR OpenCV Exitoso!' : '¡Autocompletado de Ficha Realizado!');
            triggerAlert(
                usedOpenCV ? '👁️ Escáner OMR OpenCV Activo' : '🎲 Evaluación por Clave Oficial', 
                usedOpenCV 
                    ? `${message}\n\nSe han detectado y cargado los puntajes de ${currentOMRRow.value.fullname} correctamente.` 
                    : `${message}\n\n⚠️ Nota Técnica: El motor de visión OpenCV no pudo ejecutarse. Se procedió a calificar la ficha de ${currentOMRRow.value.fullname} usando la clave de respuestas oficiales cargada en el simulacro.\n\n(Los detalles técnicos de depuración han sido impresos en la Consola del Navegador - Presione F12 para verlos)`, 
                usedOpenCV ? 'success' : 'info'
            );
        } else {
            triggerAlert('Aviso OMR', response.data.message || 'No se pudo leer la ficha óptica con precisión.', 'warning');
        }
    } catch (error) {
        console.error(error);
        triggerAlert('Error OMR', 'Ocurrió un error al intentar conectarse al servidor de visión OMR.', 'error');
    } finally {
        event.target.value = '';
        currentOMRRow.value = null;
    }
};

const formatNumber = (val) => {
    return parseFloat(val).toFixed(1);
};
</script>

<template>
    <Head title="Simulacro Examen Admisión" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PremiumPageHeader 
                title="Simulacro Examen de Admisión" 
                subtitle="Gestión integral de simulacros académicos, puntajes por nivel y cuadros de honor para ingreso." 
                icon="tabler:certificate" 
                color="indigo"
            >
                <template #actions>
                    <div class="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800/80 p-1.5 rounded-xl border border-slate-200 dark:border-slate-700/50 shadow-inner">
                        <button 
                            @click="activeTab = 'resumen'"
                            class="px-4 py-2 rounded-lg font-bold text-xs transition-all flex items-center gap-1.5"
                            :class="activeTab === 'resumen' 
                                ? 'bg-indigo-600 text-white shadow-sm' 
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'"
                        >
                            <Icon icon="tabler:layout-dashboard" class="w-4 h-4" />
                            <span>Resumen y Dashboard</span>
                        </button>
                        
                        <button 
                            @click="activeTab = 'simulacros'"
                            class="px-4 py-2 rounded-lg font-bold text-xs transition-all flex items-center gap-1.5"
                            :class="activeTab === 'simulacros' 
                                ? 'bg-indigo-600 text-white shadow-sm' 
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'"
                        >
                            <Icon icon="tabler:square-root-2" class="w-4 h-4" />
                            <span>Simulacros y Calificación</span>
                        </button>

                        <button 
                            v-if="$page.props.auth.user.id_rol <= 3 || ($page.props.auth.collaborator && $page.props.auth.collaborator.right && $page.props.auth.collaborator.right('simulacro', 'reportes'))"
                            @click="activeTab = 'reportes'"
                            class="px-4 py-2 rounded-lg font-bold text-xs transition-all flex items-center gap-1.5"
                            :class="activeTab === 'reportes' 
                                ? 'bg-indigo-600 text-white shadow-sm' 
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'"
                        >
                            <Icon icon="tabler:chart-bar" class="w-4 h-4" />
                            <span>Reportes</span>
                        </button>
                        
                        <button 
                            v-if="$page.props.auth.user.id_rol <= 3 || ($page.props.auth.collaborator && $page.props.auth.collaborator.right && $page.props.auth.collaborator.right('simulacro', 'configuracion'))"
                            @click="activeTab = 'configuracion'"
                            class="px-4 py-2 rounded-lg font-bold text-xs transition-all flex items-center gap-1.5"
                            :class="activeTab === 'configuracion' 
                                ? 'bg-indigo-600 text-white shadow-sm' 
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'"
                        >
                            <Icon icon="tabler:settings" class="w-4 h-4" />
                            <span>Configuración</span>
                        </button>
                    </div>
                </template>
            </PremiumPageHeader>
        </template>

        <div class="max-w-[1600px] mx-auto space-y-6">

            <!-- TAB 1: RESUMEN / DASHBOARD -->
            <div v-if="activeTab === 'resumen'" class="space-y-6">
                <!-- Stats Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    <div class="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm flex items-center gap-4">
                        <div class="p-4 rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 shrink-0">
                            <Icon icon="tabler:clipboard-text" class="w-6 h-6" />
                        </div>
                        <div>
                            <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Simulacros Creados</p>
                            <h3 class="text-2xl font-bold text-slate-800 dark:text-white mt-1">{{ stats.totalSimulacros }}</h3>
                        </div>
                    </div>

                    <div class="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm flex items-center gap-4">
                        <div class="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 shrink-0">
                            <Icon icon="tabler:users" class="w-6 h-6" />
                        </div>
                        <div>
                            <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Evaluados Totales</p>
                            <h3 class="text-2xl font-bold text-slate-800 dark:text-white mt-1">{{ stats.totalEvaluados }}</h3>
                        </div>
                    </div>

                    <div class="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm flex items-center gap-4">
                        <div class="p-4 rounded-2xl bg-amber-50 dark:bg-amber-900/30 text-amber-500 shrink-0">
                            <Icon icon="tabler:calculator" class="w-6 h-6" />
                        </div>
                        <div>
                            <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Puntaje Promedio</p>
                            <h3 class="text-2xl font-bold text-slate-800 dark:text-white mt-1">{{ stats.promedioPuntajes }} pts</h3>
                        </div>
                    </div>

                    <div class="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm flex items-center gap-4">
                        <div class="p-4 rounded-2xl bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 shrink-0">
                            <Icon icon="tabler:school" class="w-6 h-6" />
                        </div>
                        <div>
                            <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Tasa de Ingreso</p>
                            <h3 class="text-2xl font-bold text-slate-800 dark:text-white mt-1">{{ stats.tasaIngreso }}%</h3>
                        </div>
                    </div>
                </div>

                <!-- Cuadro de Honor / Podium -->
                <div class="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm">
                    <div class="flex items-center gap-3 mb-6">
                        <Icon icon="tabler:crown" class="w-6 h-6 text-amber-500" />
                        <div>
                            <h3 class="text-lg font-bold text-slate-800 dark:text-white">Cuadro de Honor (Top 3 por Nivel)</h3>
                            <p class="text-xs text-slate-400">Los estudiantes con los puntajes acumulados más sobresalientes de la institución.</p>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div v-for="level in parentLevels" :key="level.id" class="border border-slate-100 dark:border-slate-700/50 rounded-2xl p-5 bg-slate-50/50 dark:bg-slate-900/40">
                            <h4 class="text-md font-bold text-slate-700 dark:text-slate-200 flex items-center gap-2 mb-4">
                                <Icon icon="tabler:folder" class="w-5 h-5 text-indigo-500" />
                                {{ level.label }}
                            </h4>

                            <!-- Top 3 List -->
                            <div class="space-y-4">
                                <div 
                                    v-for="(student, idx) in (dashboardTop3[level.id] || [])" 
                                    :key="student.id" 
                                    class="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 p-4 rounded-xl flex items-center justify-between shadow-sm relative overflow-hidden group hover:shadow-md transition-all duration-300"
                                >
                                    <!-- Dynamic Left border color for Top 3 -->
                                    <div 
                                        class="absolute left-0 top-0 bottom-0 w-1.5" 
                                        :class="idx === 0 ? 'bg-amber-400' : idx === 1 ? 'bg-slate-400' : 'bg-amber-600'"
                                    ></div>
                                    
                                    <div class="flex items-center gap-3 pl-2">
                                        <!-- Podium Medal -->
                                        <div 
                                            class="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0 border"
                                            :class="idx === 0 
                                                ? 'bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-900/40 dark:border-amber-700/80 dark:text-amber-300' 
                                                : idx === 1 
                                                ? 'bg-slate-50 border-slate-300 text-slate-700 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300' 
                                                : 'bg-amber-50/50 border-amber-700/30 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800/50 dark:text-amber-400'"
                                        >
                                            {{ idx + 1 }}
                                        </div>
                                        <div>
                                            <p class="text-sm font-bold text-slate-800 dark:text-white line-clamp-1">{{ student.fullname }}</p>
                                            <p class="text-xs text-slate-400 flex items-center gap-1">
                                                <span>{{ student.level_label }}</span>
                                                <span>•</span>
                                                <span class="text-indigo-500 font-semibold">{{ student.simulacro_label }}</span>
                                            </p>
                                        </div>
                                    </div>

                                    <div class="text-right shrink-0">
                                        <p class="text-sm font-bold text-slate-800 dark:text-white">{{ student.puntaje_total }} pts</p>
                                        <span 
                                            class="text-[10px] font-bold px-2 py-0.5 rounded-full inline-block mt-1"
                                            :class="student.resultado === 1 
                                                ? 'bg-emerald-50 text-emerald-600 border border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800/80 dark:text-emerald-400' 
                                                : 'bg-rose-50 text-rose-600 border border-rose-200 dark:bg-rose-950/20 dark:border-rose-800/80 dark:text-rose-400'"
                                        >
                                            {{ student.resultado === 1 ? 'Ingresó' : 'No ingresó' }}
                                        </span>
                                    </div>
                                </div>

                                <div v-if="!(dashboardTop3[level.id] || []).length" class="text-center py-8 text-slate-400 text-xs flex flex-col items-center gap-2">
                                    <Icon icon="tabler:mood-empty" class="w-8 h-8 text-slate-300" />
                                    Aún no hay puntajes registrados para este nivel.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- TAB 2: SIMULACROS & CALIFICACIÓN -->
            <div v-if="activeTab === 'simulacros'" class="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <!-- Sidebar: Crear y Seleccionar Simulacros -->
                <div class="lg:col-span-1 space-y-6">
                    <div v-if="$page.props.auth.user.id_rol <= 3 || ($page.props.auth.collaborator && $page.props.auth.collaborator.right && $page.props.auth.collaborator.right('simulacro', 'write'))" class="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm space-y-4">
                        <h4 class="font-bold text-sm text-slate-700 dark:text-slate-200 flex items-center gap-2 border-b pb-3 border-slate-100 dark:border-slate-700">
                            <Icon icon="tabler:plus" class="w-5 h-5 text-indigo-500" />
                            Nuevo Simulacro
                        </h4>

                        <div class="space-y-3">
                            <div>
                                <label class="text-xs font-semibold text-slate-500 block mb-1">Nombre del Simulacro</label>
                                <input 
                                    v-model="newSimulacro.label" 
                                    type="text" 
                                    placeholder="Ej. 1er Simulacro UNI 2026 🚀" 
                                    class="w-full text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent py-2 px-3 focus:ring-1 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label class="text-xs font-semibold text-slate-500 block mb-1">Tipo de Examen</label>
                                <select 
                                    v-model="newSimulacro.id_tipo_examen" 
                                    class="w-full text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent py-2 px-3 focus:ring-1 focus:ring-indigo-500"
                                >
                                    <option value="" disabled>Seleccione...</option>
                                    <option v-for="t in localTiposExamen" :key="t.id" :value="t.id">{{ t.label }}</option>
                                </select>
                            </div>

                            <div>
                                <label class="text-xs font-semibold text-slate-500 block mb-1">Bimestre (Opcional)</label>
                                <select 
                                    v-model="newSimulacro.ref_ciclo" 
                                    class="w-full text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent py-2 px-3 focus:ring-1 focus:ring-indigo-500"
                                >
                                    <option value="">Ninguno</option>
                                    <option v-for="(lbl, val) in arrayBimestre" :key="val" :value="val">{{ lbl }}</option>
                                </select>
                            </div>

                            <div>
                                <label class="text-xs font-semibold text-slate-500 block mb-1">Nivel General (Opcional)</label>
                                <select 
                                    v-model="newSimulacro.id_level_parent" 
                                    class="w-full text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent py-2 px-3 focus:ring-1 focus:ring-indigo-500"
                                >
                                    <option value="">Todos los niveles</option>
                                    <option v-for="l in parentLevels" :key="l.id" :value="l.id">{{ l.label }}</option>
                                </select>
                            </div>

                            <button 
                                @click="handleStoreSimulacro"
                                class="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs py-2.5 rounded-lg transition-all flex items-center justify-center gap-2"
                            >
                                <Icon icon="tabler:plus" class="w-4 h-4" />
                                Crear e Iniciar
                            </button>
                        </div>
                    </div>

                    <!-- Lista de Simulacros -->
                    <div class="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm space-y-3">
                        <h4 class="font-bold text-sm text-slate-700 dark:text-slate-200 flex items-center gap-2 border-b pb-3 border-slate-100 dark:border-slate-700">
                            <Icon icon="tabler:list" class="w-5 h-5 text-indigo-500" />
                            Simulacros Activos
                        </h4>

                        <div class="max-h-[350px] overflow-y-auto space-y-2 pr-1">
                            <div 
                                v-for="sim in localSimulacros" 
                                :key="sim.id"
                                @click="selectSimulacro(sim)"
                                class="p-3 rounded-xl border transition-all duration-200 cursor-pointer flex justify-between items-start group"
                                :class="selectedSimulacro?.id === sim.id
                                    ? 'bg-indigo-50/50 border-indigo-200 text-indigo-900 dark:bg-indigo-950/20 dark:border-indigo-850 dark:text-indigo-300'
                                    : 'border-slate-100 hover:border-slate-200 dark:border-slate-700 dark:hover:border-slate-650'"
                            >
                                <div class="space-y-1">
                                    <p class="text-xs font-bold line-clamp-1">{{ sim.label }}</p>
                                    <div class="flex flex-wrap items-center gap-1.5 text-[10px] text-slate-400">
                                        <span class="bg-slate-100 dark:bg-slate-700/60 px-1.5 py-0.5 rounded font-medium">{{ sim.tipo_examen?.label || 'Sin Tipo' }}</span>
                                        <span v-if="sim.level_parent" class="bg-slate-100 dark:bg-slate-700/60 px-1.5 py-0.5 rounded font-medium">{{ sim.level_parent.label }}</span>
                                    </div>
                                </div>
                                <button 
                                    v-if="$page.props.auth.user.id_rol <= 3 || ($page.props.auth.collaborator && $page.props.auth.collaborator.right && $page.props.auth.collaborator.right('simulacro', 'write'))"
                                    @click.stop="handleDeleteSimulacro(sim.id)"
                                    class="text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 p-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <Icon icon="tabler:trash" class="w-4 h-4" />
                                </button>
                            </div>

                            <div v-if="!localSimulacros.length" class="text-center py-6 text-slate-400 text-xs">
                                No se encontraron simulacros creados.
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Main Section: Grid de Ingreso de Calificaciones -->
                <div class="lg:col-span-3">
                    <div class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm overflow-hidden flex flex-col h-full min-h-[500px]">
                        

                        
                        <!-- Toolbar / Header for registration and filters -->
                        <div v-if="selectedSimulacro" class="border-b border-slate-100 dark:border-slate-700 bg-slate-50/20 dark:bg-slate-800/20 p-5 space-y-4 z-20">
                            <!-- Row 1: Title Info & Primary Actions -->
                            <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <!-- Title -->
                                <div>
                                    <h3 class="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-1.5">
                                        <Icon icon="tabler:award" class="w-4 h-4 text-indigo-500" />
                                        Registro de Puntajes: <span class="text-indigo-650 dark:text-indigo-400 font-extrabold">{{ selectedSimulacro.label }}</span>
                                    </h3>
                                    <p class="text-[10px] text-slate-400 mt-0.5 font-medium">
                                        Examen: <b>{{ currentExamType?.label }}</b> 
                                        (Buenas: +{{ currentExamType?.factor_positivo }} pts | Malas: {{ currentExamType?.factor_negativo }} pts)
                                        <span v-if="selectedSimulacro.level_parent"> • Nivel: <b>{{ selectedSimulacro.level_parent.label }}</b></span>
                                    </p>
                                </div>

                                <!-- Primary Actions -->
                                <div class="flex flex-wrap items-center gap-2">
                                    <button
                                        v-if="$page.props.auth.user.id_rol <= 3 || ($page.props.auth.collaborator && $page.props.auth.collaborator.right && $page.props.auth.collaborator.right('simulacro', 'write'))" 
                                        @click="openClaveModal"
                                        class="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs py-2 px-3.5 rounded-lg flex items-center gap-1.5 shadow-sm active:scale-95 transition-all shrink-0"
                                        title="Configurar Clave de Respuestas Correctas para Calificación OMR"
                                    >
                                        <Icon icon="tabler:key" class="w-4 h-4" />
                                        <span>Clave de Respuestas</span>
                                    </button>

                                    <button 
                                        @click="exportGridToExcel"
                                        class="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs py-2 px-3.5 rounded-lg flex items-center justify-center gap-1.5 shadow-sm active:scale-95 transition-all duration-150 shrink-0"
                                        title="Exportar listado actual a archivo Excel"
                                    >
                                        <Icon icon="tabler:file-excel" class="w-4 h-4" />
                                        <span>Exportar Excel</span>
                                    </button>
                                </div>
                            </div>

                            <!-- Row 2: Add Student -->
                            <div v-if="$page.props.auth.user.id_rol <= 3 || ($page.props.auth.collaborator && $page.props.auth.collaborator.right && $page.props.auth.collaborator.right('simulacro', 'registrarestudiante'))" class="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pt-4 border-t border-slate-100 dark:border-slate-700/60">
                                <!-- Left side: Add student -->
                                <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 flex-1 relative">
                                    <!-- <span class="text-xs font-bold text-slate-500 dark:text-slate-400 whitespace-nowrap flex items-center gap-1">
                                        <Icon icon="tabler:user-plus" class="w-4 h-4 text-indigo-500" />
                                        Agregar Estudiante:
                                    </span> -->
                                    
                                    <div class="relative min-w-[240px] sm:min-w-[320px] max-w-md w-full">
                                        <div class="relative">
                                            <Icon icon="tabler:user-plus" class="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                            <input 
                                                v-model="searchStudentQuery"
                                                @focus="showStudentDropdown = true"
                                                @blur="handleStudentInputBlur"
                                                type="text"
                                                placeholder="Agregar Estudiante ..."
                                                class="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent pl-9 pr-8 py-2 focus:ring-1 focus:ring-indigo-500 text-slate-700 dark:text-slate-200"
                                            />
                                            <button 
                                                v-if="searchStudentQuery" 
                                                @click="searchStudentQuery = ''; studentToAdd = ''" 
                                                class="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                                            >
                                                <Icon icon="tabler:x" class="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                        
                                        <!-- Autocomplete Dropdown List -->
                                        <div 
                                            v-if="showStudentDropdown && filteredAddableStudents.length > 0"
                                            class="absolute left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700"
                                        >
                                            <div 
                                                v-for="s in filteredAddableStudents" 
                                                :key="s.id"
                                                @mousedown="selectStudentFromDropdown(s)"
                                                class="px-3.5 py-2 text-xs hover:bg-indigo-50 dark:hover:bg-indigo-950/40 cursor-pointer flex items-center justify-between border-b border-slate-100/50 dark:border-slate-700/50 last:border-b-0 text-slate-700 dark:text-slate-200"
                                            >
                                                <span class="font-medium">{{ s.fullname }}</span>
                                                <span class="text-[9px] bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded font-semibold">{{ s.level_label }}</span>
                                            </div>
                                        </div>
                                        <div 
                                            v-if="showStudentDropdown && filteredAddableStudents.length === 0"
                                            class="absolute left-0 right-0 mt-1 p-3 text-center text-xs text-slate-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50"
                                        >
                                            No se encontraron alumnos disponibles
                                        </div>
                                    </div>
                                    
                                    <button 
                                        @click="addStudent"
                                        :disabled="!studentToAdd || isAddingStudent"
                                        class="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:text-slate-400 text-white font-bold text-xs py-2 px-4 rounded-lg flex items-center justify-center gap-1.5 shadow-sm active:scale-95 transition-all duration-150 shrink-0"
                                    >
                                        <Icon v-if="isAddingStudent" icon="tabler:loader" class="w-3.5 h-3.5 animate-spin" />
                                        <Icon v-else icon="tabler:plus" class="w-3.5 h-3.5" />
                                        <span>Agregar</span>
                                    </button>
                                </div>

                                <!-- Right side: Filters -->
                                <div class="flex flex-col sm:flex-row items-center gap-2 shrink-0">
                                    <button 
                                        @click="bulkAddStudents"
                                        :disabled="isBulkAdding"
                                        class="border border-indigo-200 dark:border-indigo-900/60 bg-indigo-50 dark:bg-indigo-950/20 hover:bg-indigo-100 dark:hover:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 font-bold text-xs py-2 px-3.5 rounded-lg flex items-center justify-center gap-1.5 shadow-sm active:scale-95 transition-all duration-150 disabled:opacity-50"
                                        title="Precargar todos los alumnos activos de este nivel escolar en este simulacro"
                                    >
                                        <Icon v-if="isBulkAdding" icon="tabler:loader" class="w-3.5 h-3.5 animate-spin" />
                                        <Icon v-else icon="tabler:users-plus" class="w-3.5 h-3.5" />
                                        <span>Cargar todos los alumnos</span>
                                    </button>
                                </div>
                            </div>
                            <!-- Row 3: Filters -->
                            <div class="flex flex-col sm:flex-row items-center gap-2 shrink-0">
                                <!-- Search Filter -->
                                <div class="relative w-full sm:w-48">
                                    <Icon icon="tabler:search" class="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <input 
                                        v-model="studentFilter" 
                                        type="text" 
                                        placeholder="Buscar alumno..." 
                                        class="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent pl-9 pr-3 py-2 focus:ring-1 focus:ring-indigo-500 text-slate-700 dark:text-slate-200"
                                    />
                                </div>

                                <!-- Grade Filter -->
                                <div class="relative w-full sm:w-40">
                                    <select 
                                        v-model="gradeFilter" 
                                        class="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent py-2 pl-3 pr-8 focus:ring-1 focus:ring-indigo-500 appearance-none text-slate-700 dark:text-slate-200"
                                    >
                                        <option value="" class="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200">Todos los grados</option>
                                        <option 
                                            v-for="grade in availableGrades" 
                                            :key="grade" 
                                            :value="grade"
                                            class="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                                        >
                                            {{ grade }}
                                        </option>
                                    </select>
                                    <Icon icon="tabler:chevron-down" class="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                                </div>
                            </div>
                        </div>

                        <!-- Grading Grid -->
                        <!-- Top scrollbar -->
                        <div 
                            v-if="selectedSimulacro && tableWidth > 0"
                            ref="topScrollContainer"
                            @scroll="syncScrollTop"
                            class="w-full overflow-x-auto border-b border-slate-100 dark:border-slate-700/50 bg-slate-50/20 dark:bg-slate-900/10 scrollbar-thin"
                            style="height: 10px;"
                        >
                            <div :style="{ width: tableWidth + 'px', height: '1px' }"></div>
                        </div>

                        <div 
                            ref="scrollContainer"
                            @scroll="syncScrollBottom"
                            class="flex-1 overflow-x-auto relative"
                        >
                            <div v-if="loadingResultados" class="absolute inset-0 bg-white/70 dark:bg-slate-800/70 z-10 flex items-center justify-center flex-col gap-2">
                                <Icon icon="tabler:loader" class="w-8 h-8 animate-spin text-indigo-600" />
                                <span class="text-xs font-semibold text-slate-500">Cargando alumnos...</span>
                            </div>

                            <div v-if="!selectedSimulacro" class="h-full flex flex-col items-center justify-center text-slate-400 py-16 gap-3">
                                <Icon icon="tabler:square-root-2" class="w-12 h-12 text-slate-300" />
                                <p class="text-sm font-semibold">Seleccione un simulacro de la lista para empezar a registrar los puntajes.</p>
                            </div>

                            <table v-else ref="tableElement" class="w-full text-left border-collapse min-w-[1000px]">
                                <thead>
                                    <tr class="bg-slate-50/50 dark:bg-slate-900/30 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-100 dark:border-slate-700/80">
                                        <th class="p-4 pl-6 min-w-[200px]">Estudiante</th>
                                        <th class="p-4">Grado</th>
                                        <!-- Dynamic course headers -->
                                        <th v-for="curso in currentExamType?.cursos" :key="curso.id" class="p-4 text-center border-l border-slate-100 dark:border-slate-700/50">
                                            <div>{{ curso.label }}</div>
                                            <div class="text-[9px] text-slate-400 lowercase font-medium">({{ curso.cantidad_preguntas }} preguntas)</div>
                                        </th>
                                        <th class="p-4 text-center border-l border-slate-100 dark:border-slate-700/50">Resumen</th>
                                        <th class="p-4 text-center">Puntaje</th>
                                        <th class="p-4 text-center">Estado</th>
                                        <th class="p-4 text-center pr-6">Acción</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-slate-100 dark:divide-slate-700/50 text-xs">
                                    <tr 
                                        v-for="(row, rIdx) in filteredResultados" 
                                        :key="row.id" 
                                        class="hover:bg-slate-50/40 dark:hover:bg-slate-700/20 transition-colors"
                                    >
                                        <!-- Estudiante -->
                                        <td class="p-4 pl-6 font-bold text-slate-800 dark:text-slate-200">
                                            {{ row.fullname }}
                                        </td>
                                        
                                        <!-- Grado -->
                                        <td class="p-4 text-slate-500 whitespace-nowrap">
                                            {{ row.level_label }}
                                        </td>

                                        <!-- Dynamic Course Inputs -->
                                        <td 
                                            v-for="(curso, cIdx) in currentExamType?.cursos" 
                                            :key="curso.id" 
                                            class="p-4 border-l border-slate-100 dark:border-slate-700/50 whitespace-nowrap"
                                        >
                                            <template v-if="!row.detalles_editables[curso.id]">
                                                <span class="text-xs text-slate-400 italic">—</span>
                                            </template>
                                            <template v-else>
                                            <div class="flex items-center gap-1.5 justify-center">
                                                <!-- Buenas Input -->
                                                <div class="w-12 text-center">
                                                    <span class="text-[9px] font-semibold text-slate-400 block mb-0.5">B</span>
                                                    <input 
                                                        v-model.number="row.detalles_editables[curso.id].buenas" 
                                                        type="number"
                                                        min="0"
                                                        :max="curso.cantidad_preguntas"
                                                        @input="calculateRowDynamic(row, curso.id, 'buenas')"
                                                        @keydown="handleKeyNavigation($event, rIdx, cIdx, 'buenas')"
                                                        :id="`input-buenas-${rIdx}-${cIdx}`"
                                                        class="w-full text-center text-xs py-1 px-0.5 rounded border border-slate-200 dark:border-slate-700 bg-transparent text-emerald-600 dark:text-emerald-400 font-bold focus:ring-1 focus:ring-indigo-500"
                                                    />
                                                </div>
                                                
                                                <!-- Malas Input -->
                                                <div class="w-12 text-center">
                                                    <span class="text-[9px] font-semibold text-slate-400 block mb-0.5">M</span>
                                                    <input 
                                                        v-model.number="row.detalles_editables[curso.id].malas" 
                                                        type="number"
                                                        min="0"
                                                        :max="curso.cantidad_preguntas"
                                                        @input="calculateRowDynamic(row, curso.id, 'malas')"
                                                        @keydown="handleKeyNavigation($event, rIdx, cIdx, 'malas')"
                                                        :id="`input-malas-${rIdx}-${cIdx}`"
                                                        class="w-full text-center text-xs py-1 px-0.5 rounded border border-slate-200 dark:border-slate-700 bg-transparent text-rose-600 dark:text-rose-400 font-bold focus:ring-1 focus:ring-indigo-500"
                                                    />
                                                </div>

                                                <!-- No contesto -->
                                                <div class="w-10 text-center">
                                                    <span class="text-[9px] font-semibold text-slate-400 block mb-0.5">NC</span>
                                                    <div class="text-xs font-semibold py-1 text-slate-400 dark:text-slate-500">
                                                        {{ row.detalles_editables[curso.id].no_contesto }}
                                                    </div>
                                                </div>

                                                <!-- Efficiency Badge -->
                                                <div class="text-center shrink-0 ml-1">
                                                    <span class="text-[9px] font-semibold text-slate-400 block mb-0.5">Ef.</span>
                                                    <span class="text-[10px] font-bold text-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded">
                                                        {{ row.detalles_editables[curso.id].eficiencia }}%
                                                    </span>
                                                </div>
                                            </div>
                                            </template>
                                        </td>

                                        <!-- Resumen (Total Buenas/Malas) -->
                                        <td class="p-4 border-l border-slate-100 dark:border-slate-700/50 text-center whitespace-nowrap">
                                            <div class="flex items-center justify-center gap-1.5">
                                                <span class="bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded font-bold text-[10px]">
                                                    {{ row.total_buenas }} ok
                                                </span>
                                                <span class="bg-rose-50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400 px-1.5 py-0.5 rounded font-bold text-[10px]">
                                                    {{ row.total_malas }} err
                                                </span>
                                            </div>
                                        </td>

                                        <!-- Puntaje Total -->
                                        <td class="p-4 text-center font-extrabold whitespace-nowrap text-slate-700 dark:text-slate-200">
                                            {{ row.puntaje_total }} pts
                                        </td>

                                        <!-- Resultado (Ingreso Status) -->
                                        <td class="p-4 text-center whitespace-nowrap">
                                            <span 
                                                class="font-bold text-[10px] px-2.5 py-1 rounded-full border"
                                                :class="row.resultado === 1 
                                                    ? 'bg-emerald-50 border-emerald-200 text-emerald-600 dark:bg-emerald-950/20 dark:border-emerald-800/80 dark:text-emerald-400' 
                                                    : 'bg-rose-50 border-rose-200 text-rose-600 dark:bg-rose-950/20 dark:border-rose-800/80 dark:text-rose-400'"
                                            >
                                                {{ row.resultado === 1 ? 'Ingresó' : 'No ingresó' }}
                                            </span>
                                            <div class="text-[9px] text-slate-400 mt-1">mín: {{ row.simulacro_min_score }} pts</div>
                                        </td>

                                        <!-- Guardar y OMR -->
                                        <td class="p-4 text-center pr-6 whitespace-nowrap">
                                            <div class="flex items-center justify-center gap-1.5 mx-auto w-fit">
                                                <button 
                                                    @click="saveRowResult(row)"
                                                    :disabled="savingRowId === row.id"
                                                    class="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-3 rounded-lg font-semibold text-xs shadow-sm hover:shadow transition-all duration-200 flex items-center justify-center gap-1 min-w-[70px] disabled:opacity-50"
                                                >
                                                    <Icon v-if="savingRowId === row.id" icon="tabler:loader" class="w-3.5 h-3.5 animate-spin" />
                                                    <Icon v-else icon="tabler:device-floppy" class="w-3.5 h-3.5" />
                                                    <span>{{ savingRowId === row.id ? '...' : 'Guardar' }}</span>
                                                </button>
                                                
                                                <button 
                                                    v-if="confGlobal?.ACADEMICO_USE_SIMULACRO_ADMISION_OMR == 1"
                                                    @click="triggerOMRUpload(row)"
                                                    class="bg-emerald-600 hover:bg-emerald-700 text-white p-2 rounded-lg font-semibold text-xs shadow-sm hover:shadow transition-all duration-200 flex items-center justify-center gap-1 active:scale-95"
                                                    title="Escanear Ficha Óptica (OMR)"
                                                >
                                                    <Icon icon="tabler:camera" class="w-4 h-4" />
                                                </button>
                                                
                                                <button 
                                                    @click="deleteRowResult(row)"
                                                    class="bg-rose-500 hover:bg-rose-600 text-white p-2 rounded-lg font-semibold text-xs shadow-sm hover:shadow transition-all duration-200 flex items-center justify-center gap-1 active:scale-95"
                                                    title="Retirar alumno del simulacro"
                                                >
                                                    <Icon icon="tabler:trash" class="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    
                                    <tr v-if="!filteredResultados.length">
                                        <td :colspan="6 + (currentExamType?.cursos.length || 0)" class="p-8 text-center text-slate-400 text-xs">
                                            No se encontraron estudiantes para los criterios de este simulacro.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <!-- TAB 3: CONFIGURACIÓN -->
            <div v-if="activeTab === 'configuracion' && ($page.props.auth.user.id_rol <= 3 || ($page.props.auth.collaborator && $page.props.auth.collaborator.right && $page.props.auth.collaborator.right('simulacro', 'configuracion')))" class="space-y-6">
                <!-- Row: Tipos de Examen y Cursos -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <!-- Configurar Tipos de Examen -->
                    <div class="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm space-y-6">
                        <div class="flex items-center justify-between border-b pb-3 border-slate-100 dark:border-slate-700">
                            <h3 class="text-md font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <Icon icon="tabler:certificate" class="w-5 h-5 text-indigo-500" />
                                Tipos de Examen de Admisión
                            </h3>
                        </div>

                        <!-- Form Agregar Tipo -->
                        <div class="p-4 bg-slate-50/50 dark:bg-slate-900/40 rounded-xl space-y-3">
                            <h4 class="text-xs font-bold text-slate-600 dark:text-slate-300">Registrar Nuevo Tipo</h4>
                            <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
                                <div>
                                    <label class="text-[10px] font-semibold text-slate-400 block mb-0.5">Nombre (UNI, San Marcos, etc.)</label>
                                    <input 
                                        v-model="newTipo.label" 
                                        type="text" 
                                        placeholder="Ej. UNI" 
                                        class="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent py-2 px-3 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label class="text-[10px] font-semibold text-slate-400 block mb-0.5">Puntaje Correcto (+)</label>
                                    <input 
                                        v-model.number="newTipo.factor_positivo" 
                                        type="number" 
                                        step="0.01"
                                        class="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent py-2 px-3 focus:ring-1 focus:ring-indigo-500 font-semibold"
                                    />
                                </div>
                                <div>
                                    <label class="text-[10px] font-semibold text-slate-400 block mb-0.5">Puntaje Incorrecto (-)</label>
                                    <input 
                                        v-model.number="newTipo.factor_negativo" 
                                        type="number" 
                                        step="0.001"
                                        class="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent py-2 px-3 focus:ring-1 focus:ring-indigo-500 font-semibold"
                                    />
                                </div>
                                <div>
                                    <label class="text-[10px] font-semibold text-slate-400 block mb-0.5">Plantilla PDF Ficha</label>
                                    <input 
                                        ref="newTipoFile"
                                        type="file" 
                                        accept=".pdf" 
                                        class="w-full text-[10px] text-slate-500 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent py-1.5 px-3 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>
                            <div class="flex justify-end mt-2">
                                <button 
                                    @click="handleStoreTipo"
                                    class="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs py-2 px-4 rounded-lg flex items-center gap-1.5 transition-all shadow-sm"
                                >
                                    <Icon icon="tabler:plus" class="w-4 h-4" />
                                    Agregar Tipo
                                </button>
                            </div>
                        </div>

                        <!-- Tabla de Tipos -->
                        <div class="overflow-x-auto">
                            <table class="w-full text-left border-collapse">
                                <thead>
                                    <tr class="text-[10px] font-bold text-slate-400 uppercase border-b border-slate-100 dark:border-slate-700 pb-2">
                                        <th class="pb-2">Tipo de Examen</th>
                                        <th class="pb-2 text-center">Factor Correcto</th>
                                        <th class="pb-2 text-center">Factor Incorrecto</th>
                                        <th class="pb-2 text-right">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-slate-100 dark:divide-slate-700/50 text-xs">
                                    <tr v-for="tipo in localTiposExamen" :key="tipo.id" class="hover:bg-slate-50/50 dark:hover:bg-slate-700/20">
                                        <td class="py-3">
                                            <div v-if="editingTipo?.id === tipo.id" class="flex flex-col gap-1 w-full">
                                                <input v-model="tipo.label" class="text-xs p-1 border rounded w-full bg-transparent mb-1" />
                                                <label class="text-[9px] font-bold text-slate-400">Actualizar PDF Ficha (Opcional):</label>
                                                <input 
                                                    :id="`update-pdf-file-${tipo.id}`" 
                                                    type="file" 
                                                    accept=".pdf" 
                                                    class="text-[10px] w-full text-slate-500 font-semibold"
                                                />
                                            </div>
                                            <div v-else class="flex items-center gap-2">
                                                <span class="font-bold text-slate-700 dark:text-slate-200">{{ tipo.label }}</span>
                                                <a 
                                                    v-if="tipo.template_pdf_path" 
                                                    :href="`/storage/${tipo.template_pdf_path}`" 
                                                    target="_blank"
                                                    class="text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5 ml-1"
                                                    title="Descargar Ficha Impresión (PDF)"
                                                >
                                                    <Icon icon="tabler:file-download" class="w-4 h-4 text-emerald-500 hover:scale-110 transition-transform" />
                                                </a>
                                            </div>
                                        </td>
                                        
                                        <td class="py-3 text-center">
                                            <input 
                                                v-if="editingTipo?.id === tipo.id" 
                                                v-model.number="tipo.factor_positivo" 
                                                type="number" step="0.01" 
                                                class="text-xs p-1 border rounded w-16 text-center bg-transparent" 
                                            />
                                            <span v-else class="bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 font-bold px-2.5 py-0.5 rounded">
                                                +{{ tipo.factor_positivo }}
                                            </span>
                                        </td>

                                        <td class="py-3 text-center">
                                            <input 
                                                v-if="editingTipo?.id === tipo.id" 
                                                v-model.number="tipo.factor_negativo" 
                                                type="number" step="0.001" 
                                                class="text-xs p-1 border rounded w-20 text-center bg-transparent" 
                                            />
                                            <span v-else class="bg-rose-50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400 font-bold px-2.5 py-0.5 rounded">
                                                {{ tipo.factor_negativo }}
                                            </span>
                                        </td>

                                        <td class="py-3 text-right whitespace-nowrap">
                                            <div v-if="editingTipo?.id === tipo.id" class="flex gap-1.5 justify-end">
                                                <button @click="handleUpdateTipo(tipo)" class="bg-emerald-500 hover:bg-emerald-600 text-white p-1 rounded-lg">
                                                    <Icon icon="tabler:check" class="w-4 h-4" />
                                                </button>
                                                <button @click="editingTipo = null" class="bg-slate-400 hover:bg-slate-500 text-white p-1 rounded-lg">
                                                    <Icon icon="tabler:x" class="w-4 h-4" />
                                                </button>
                                            </div>
                                            <div v-else class="flex gap-1.5 justify-end">
                                                <button @click="editingTipo = tipo" class="text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 p-1.5 rounded-lg">
                                                    <Icon icon="tabler:pencil" class="w-4.5 h-4.5" />
                                                </button>
                                                <button @click="handleDeleteTipo(tipo.id)" class="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 p-1.5 rounded-lg">
                                                    <Icon icon="tabler:trash" class="w-4.5 h-4.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Configurar Cursos por Examen -->
                    <div class="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm space-y-6">
                        <div class="flex items-center justify-between border-b pb-3 border-slate-100 dark:border-slate-700">
                            <h3 class="text-md font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <Icon icon="tabler:book-2" class="w-5 h-5 text-indigo-500" />
                                Asignación de Cursos y Estructuras
                            </h3>
                        </div>

                        <!-- Form Agregar Curso -->
                        <div class="p-4 bg-slate-50/50 dark:bg-slate-900/40 rounded-xl space-y-3">
                            <h4 class="text-xs font-bold text-slate-600 dark:text-slate-300">Asignar Nuevo Curso a Examen</h4>
                            <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
                                <div>
                                    <label class="text-[10px] font-semibold text-slate-400 block mb-0.5">Pertenece a Tipo Examen</label>
                                    <select 
                                        v-model="newCurso.id_tipo_examen" 
                                        class="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent py-2 px-3 focus:ring-1 focus:ring-indigo-500"
                                    >
                                        <option value="" disabled>Seleccione...</option>
                                        <option v-for="t in localTiposExamen" :key="t.id" :value="t.id">{{ t.label }}</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="text-[10px] font-semibold text-slate-400 block mb-0.5">Área Académica</label>
                                    <select 
                                        v-model="newCurso.id_area" 
                                        class="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent py-2 px-3 focus:ring-1 focus:ring-indigo-500"
                                    >
                                        <option value="">General / Ninguna</option>
                                        <option v-for="a in areasCursos" :key="a.id" :value="a.id">{{ a.label }}</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="text-[10px] font-semibold text-slate-400 block mb-0.5">Nombre Curso</label>
                                    <input 
                                        v-model="newCurso.label" 
                                        type="text" 
                                        placeholder="Ej. Álgebra" 
                                        class="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent py-2 px-3 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label class="text-[10px] font-semibold text-slate-400 block mb-0.5">Cantidad de Preguntas</label>
                                    <input 
                                        v-model.number="newCurso.cantidad_preguntas" 
                                        type="number" 
                                        min="1"
                                        class="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent py-2 px-3 focus:ring-1 focus:ring-indigo-500 font-semibold"
                                    />
                                </div>
                            </div>
                            <div class="flex justify-end mt-2">
                                <button 
                                    @click="handleStoreCurso"
                                    class="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs py-2 px-4 rounded-lg flex items-center gap-1.5 transition-all shadow-sm"
                                >
                                    <Icon icon="tabler:plus" class="w-4 h-4" />
                                    Asociar Curso
                                </button>
                            </div>
                        </div>

                        <!-- Lista de cursos agrupados por Examen -->
                        <div class="space-y-4 max-h-[300px] overflow-y-auto pr-1">
                            <div v-for="tipo in localTiposExamen" :key="tipo.id" class="border border-slate-100 dark:border-slate-700/80 rounded-xl p-3 bg-slate-50/20 dark:bg-slate-900/10">
                                <h4 class="text-xs font-bold text-slate-700 dark:text-slate-300 border-b pb-1.5 flex items-center justify-between">
                                    <span>Examen: <b class="text-indigo-600 dark:text-indigo-400">{{ tipo.label }}</b></span>
                                    <span class="text-[9px] bg-slate-100 dark:bg-slate-750 px-2 py-0.5 rounded">{{ tipo.cursos?.length || 0 }} cursos</span>
                                </h4>
                                <div class="flex flex-wrap gap-2 mt-2">
                                    <div 
                                        v-for="c in tipo.cursos" 
                                        :key="c.id" 
                                        class="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 pl-3 pr-2 py-1.5 rounded-xl text-xs shadow-sm"
                                    >
                                        <span class="font-bold text-slate-800 dark:text-slate-200">{{ c.label }}</span>
                                        <span v-if="c.area" class="bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-md text-[9px] font-extrabold uppercase tracking-wide">
                                            {{ c.area.label }}
                                        </span>
                                        <span class="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-350 px-2 py-0.5 rounded-md text-[9px] font-bold">
                                            {{ c.cantidad_preguntas }} preguntas
                                        </span>
                                        <button 
                                            @click="handleDeleteCurso(tipo.id, c.id)" 
                                            class="text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 p-1 rounded-lg transition-colors ml-1"
                                        >
                                            <Icon icon="tabler:x" class="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    <div v-if="!tipo.cursos?.length" class="text-[10px] text-slate-400 italic py-1 pl-1">
                                        No hay cursos asociados a este tipo de examen.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Configuración de Puntajes Máximo y Mínimo por Nivel (Grados) -->
                <div class="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm space-y-6">
                    <div class="flex items-center justify-between border-b pb-3 border-slate-100 dark:border-slate-700">
                        <h3 class="text-md font-bold text-slate-800 dark:text-white flex items-center gap-2">
                            <Icon icon="tabler:adjustments-horizontal" class="w-5 h-5 text-indigo-500" />
                            Configuración de Puntajes por Grado / Nivel Académico
                        </h3>
                        <p class="text-xs text-slate-400">Personaliza la nota máxima factible y el mínimo aprobatorio (puntaje de ingreso) para cada grado escolar.</p>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-h-[500px] overflow-y-auto pr-1">
                        <div 
                            v-for="lvl in localChildLevels" 
                            :key="lvl.id" 
                            class="border border-slate-100 dark:border-slate-700/80 p-4 rounded-2xl bg-slate-50/40 dark:bg-slate-900/30 space-y-3 relative group hover:border-slate-200 transition-all"
                        >
                            <h4 class="text-xs font-extrabold text-slate-700 dark:text-slate-350 flex items-center gap-1.5">
                                <Icon icon="tabler:folder" class="w-4 h-4 text-indigo-500 shrink-0" />
                                <span>{{ lvl.label }}</span>
                            </h4>

                            <div class="grid grid-cols-2 gap-3">
                                <div>
                                    <label class="text-[9px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">Puntaje Máximo</label>
                                    <input 
                                        v-model.number="lvl.simulacro_max_score"
                                        type="number"
                                        :disabled="levelConfigEditing?.id !== lvl.id"
                                        class="w-full text-xs font-bold py-1.5 px-2 rounded-lg border bg-transparent text-slate-800 dark:text-slate-100 disabled:opacity-75 focus:ring-1 focus:ring-indigo-500"
                                        :class="levelConfigEditing?.id === lvl.id ? 'border-indigo-500' : 'border-slate-200 dark:border-slate-750'"
                                    />
                                </div>
                                <div>
                                    <label class="text-[9px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">Puntaje de Ingreso</label>
                                    <input 
                                        v-model.number="lvl.simulacro_min_score"
                                        type="number"
                                        :disabled="levelConfigEditing?.id !== lvl.id"
                                        class="w-full text-xs font-bold py-1.5 px-2 rounded-lg border bg-transparent text-indigo-600 dark:text-indigo-400 disabled:opacity-75 focus:ring-1 focus:ring-indigo-500"
                                        :class="levelConfigEditing?.id === lvl.id ? 'border-indigo-500' : 'border-slate-200 dark:border-slate-750'"
                                    />
                                </div>
                            </div>

                            <div class="flex justify-end pt-1">
                                <div v-if="levelConfigEditing?.id === lvl.id" class="flex gap-2">
                                    <button 
                                        @click="handleUpdateLevelConfig(lvl)"
                                        class="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-[10px] px-3 py-1 rounded-md shadow-sm"
                                    >
                                        Guardar
                                    </button>
                                    <button 
                                        @click="levelConfigEditing = null"
                                        class="bg-slate-300 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold text-[10px] px-3 py-1 rounded-md"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                                <button 
                                    v-else 
                                    @click="levelConfigEditing = lvl"
                                    class="text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/20 font-bold text-[10px] px-2.5 py-1 rounded-md transition-colors"
                                >
                                    Modificar Puntajes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- TAB 4: REPORTES -->
            <div v-if="activeTab === 'reportes' && ($page.props.auth.user.id_rol <= 3 || ($page.props.auth.collaborator && $page.props.auth.collaborator.right && $page.props.auth.collaborator.right('simulacro', 'reportes')))" class="space-y-6 animate-fade-in">
                <!-- Subnavigation Tabs -->
                <div class="flex items-center gap-2 border-b border-slate-100 dark:border-slate-700 pb-1">
                    <button 
                        @click="reportSubTab = 'alumno'"
                        class="px-4 py-2 text-xs font-bold transition-all duration-200 border-b-2 -mb-[5px] flex items-center gap-1.5"
                        :class="reportSubTab === 'alumno' 
                            ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400' 
                            : 'border-transparent text-slate-500 hover:text-slate-755 dark:text-slate-400'"
                    >
                        <Icon icon="tabler:user-search" class="w-4 h-4" />
                        Reportes por Alumno
                    </button>
                    <button 
                        @click="reportSubTab = 'nivel'"
                        class="px-4 py-2 text-xs font-bold transition-all duration-200 border-b-2 -mb-[5px] flex items-center gap-1.5"
                        :class="reportSubTab === 'nivel' 
                            ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400' 
                            : 'border-transparent text-slate-500 hover:text-slate-755 dark:text-slate-400'"
                    >
                        <Icon icon="tabler:school" class="w-4 h-4" />
                        Reportes por Nivel
                    </button>
                </div>

                <!-- SUBTAB 4.1: REPORTES POR ALUMNO -->
                <div v-if="reportSubTab === 'alumno'" class="space-y-6">
                    <!-- Search and Selector Box -->
                    <div class="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm space-y-4">
                        <h3 class="text-md font-bold text-slate-800 dark:text-white flex items-center gap-2">
                            <Icon icon="tabler:user-search" class="w-5 h-5 text-indigo-500" />
                            Seleccionar Estudiante para Reporte Académico
                        </h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                            <div>
                                <label class="text-xs font-semibold text-slate-400 block mb-1.5">Estudiante Activo</label>
                                <select 
                                    v-model="selectedReportStudentId" 
                                    @change="loadStudentReport"
                                    class="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-transparent py-2.5 px-4 focus:ring-1 focus:ring-indigo-500 text-slate-800 dark:text-slate-100"
                                >
                                    <option value="" class="dark:bg-slate-800 text-slate-400">Seleccione un estudiante...</option>
                                    <option v-for="std in studentsList" :key="std.id" :value="std.id" class="dark:bg-slate-800 text-slate-750 dark:text-slate-200">
                                        {{ std.fullname }} ({{ std.level_label }})
                                    </option>
                                </select>
                            </div>
                            <div class="flex items-center justify-between">
                                <div class="w-4"></div>
                                <div v-if="loadingReport" class="text-xs font-semibold text-slate-400 flex items-center gap-1.5">
                                    <Icon icon="tabler:loader" class="w-5 h-5 animate-spin" />
                                    <span>Cargando análisis académico...</span>
                                </div>
                                <button 
                                    v-else-if="reportData" 
                                    @click="printReport"
                                    class="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs py-2.5 px-4 rounded-xl flex items-center gap-1.5 transition-all shadow-sm active:scale-95"
                                >
                                    <Icon icon="tabler:printer" class="w-4 h-4" />
                                    Exportar a PDF / Imprimir
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- If report data loaded -->
                    <div v-if="reportData" id="print-report-section" class="space-y-6">
                        
                        <!-- PRINT-ONLY HEADER -->
                        <div class="hidden print:block border-b-2 border-slate-900 pb-4 mb-6">
                            <div class="flex items-center justify-between">
                                <div>
                                    <h1 class="text-xl font-black text-slate-900 uppercase">Reporte de Admisión y Simulacros</h1>
                                    <p class="text-xs text-slate-500 mt-1">Análisis Integral de Evolución y Rendimiento por Estudiante</p>
                                </div>
                                <div class="text-right text-xs text-slate-500">
                                    <p class="font-bold">Fecha de Reporte: <span class="font-medium">{{ new Date().toLocaleDateString() }}</span></p>
                                    <p class="font-bold mt-0.5">Sistema de Gestión Escolar</p>
                                </div>
                            </div>
                            <div class="mt-4 bg-slate-550/10 p-4 rounded-xl border border-slate-200 grid grid-cols-2 gap-4 text-xs text-slate-800">
                                <div>
                                    <p class="font-bold">Estudiante: <span class="font-medium">{{ getStudentName(selectedReportStudentId) }}</span></p>
                                </div>
                                <div>
                                    <p class="font-bold">Nivel / Grado: <span class="font-medium">{{ getStudentLevel(selectedReportStudentId) }}</span></p>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Diagnosis & Overall stats -->
                        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 print-break-inside-avoid">
                            <!-- Academic Diagnosis Panel -->
                            <div class="lg:col-span-2 bg-gradient-to-br from-indigo-50/50 to-white dark:from-slate-900/60 dark:to-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm flex flex-col justify-between space-y-6">
                                <div>
                                    <h4 class="text-md font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                        <Icon icon="tabler:activity-heartbeat" class="w-5 h-5 text-indigo-500" />
                                        Diagnóstico Académico Integral
                                    </h4>
                                    <p class="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mt-4 bg-white/70 dark:bg-slate-950/40 p-4 rounded-xl border border-slate-100 dark:border-slate-750/30" v-html="reportData.diagnosis"></p>
                                </div>
                                
                                <div class="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-slate-100 dark:border-slate-700/50">
                                    <div class="p-3 bg-white dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-750 shadow-sm">
                                        <span class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">Participaciones</span>
                                        <span class="text-2xl font-black text-slate-800 dark:text-white block mt-0.5">{{ reportData.resultados.length }}</span>
                                    </div>
                                    <div class="p-3 bg-white dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-750 shadow-sm">
                                        <span class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">Tasa de Ingreso</span>
                                        <span class="text-2xl font-black text-emerald-600 dark:text-emerald-400 block mt-0.5">
                                            {{ reportData.resultados.length ? formatNumber((reportData.resultados.filter(r => r.resultado === 1).length * 100) / reportData.resultados.length) : 0 }}%
                                        </span>
                                    </div>
                                    <div class="p-3 bg-white dark:bg-slate-800/80 rounded-xl border border-slate-100 dark:border-slate-750 shadow-sm col-span-2 md:col-span-1">
                                        <span class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">Puntaje Promedio</span>
                                        <span class="text-2xl font-black text-indigo-600 dark:text-indigo-400 block mt-0.5">
                                            {{ reportData.resultados.length ? formatNumber(reportData.resultados.reduce((acc, r) => acc + r.puntaje_total, 0) / reportData.resultados.length) : 0 }} pts
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- OMR Diagnostics -->
                            <div class="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm flex flex-col justify-between space-y-6">
                                <div>
                                    <h4 class="text-md font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                        <Icon icon="tabler:target-arrow" class="w-5 h-5 text-indigo-500" />
                                        Rendimiento General
                                    </h4>
                                    <div class="h-[200px] flex items-center justify-center mt-4 print:h-[180px]">
                                        <RadarChartExample :data="radarChartData" />
                                    </div>
                                </div>
                                <div class="bg-slate-50 dark:bg-slate-900/40 p-4 rounded-xl border border-slate-100 dark:border-slate-750/30 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                                    <p class="font-bold text-slate-700 dark:text-slate-300 mb-1">Nota Metodológica:</p>
                                    La gráfica representa el porcentaje de eficiencia (respuestas correctas respecto al total) agrupado por áreas de conocimiento.
                                </div>
                            </div>
                        </div>

                        <!-- Progress Charts -->
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 print-break-inside-avoid">
                            <div class="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm">
                                <h4 class="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2 mb-4">
                                    <Icon icon="tabler:chart-line" class="w-5 h-5 text-indigo-500" />
                                    Evolución de Puntaje Total
                                </h4>
                                <div class="h-[250px] flex items-center justify-center">
                                    <BarChart :data="barChartData" />
                                </div>
                            </div>

                            <div class="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm flex flex-col gap-4">
                                <h4 class="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <Icon icon="tabler:chart-arrows-vertical" class="w-5 h-5 text-emerald-500" />
                                    Evolución de Eficiencia por Área
                                </h4>
                                <div class="h-[220px] flex items-center justify-center">
                                    <LineChart :data="lineChartData" />
                                </div>
                                <!-- Legend -->
                                <div 
                                    v-if="lineChartData.datasets && lineChartData.datasets.length"
                                    class="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 pt-3 border-t border-slate-100 dark:border-slate-700/50"
                                >
                                    <div
                                        v-for="ds in lineChartData.datasets"
                                        :key="ds.label"
                                        class="flex items-center gap-1.5"
                                    >
                                        <span
                                            class="inline-block w-3 h-3 rounded-full border-2 shrink-0"
                                            :style="{ borderColor: ds.borderColor, backgroundColor: ds.backgroundColor }"
                                        ></span>
                                        <span class="text-[11px] font-semibold text-slate-600 dark:text-slate-300">{{ ds.label }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- History and Details Table -->
                        <div class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm overflow-hidden print-break-inside-avoid">
                            <div class="p-6 border-b border-slate-100 dark:border-slate-700">
                                <h4 class="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <Icon icon="tabler:list-details" class="w-5 h-5 text-indigo-500" />
                                    Historial y Desglose Detallado de Simulacros
                                </h4>
                            </div>
                            <div class="overflow-x-auto">
                                <table class="w-full text-left text-xs border-collapse">
                                    <thead>
                                        <tr class="bg-slate-50 dark:bg-slate-900/60 text-slate-450 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-700">
                                            <th class="py-4 px-6">Simulacro</th>
                                            <th class="py-4 px-4">Tipo Examen</th>
                                            <th class="py-4 px-4 text-center">Respuestas (B / M / N)</th>
                                            <th class="py-4 px-4 text-right">Puntaje Total</th>
                                            <th class="py-4 px-6 text-center">Resultado</th>
                                            <th class="py-4 px-6">Eficiencia por Área</th>
                                        </tr>
                                    </thead>
                                    <tbody class="divide-y divide-slate-100 dark:divide-slate-700/60">
                                        <tr v-for="res in reportData.resultados" :key="res.id" class="hover:bg-slate-50/40 dark:hover:bg-slate-900/20">
                                            <td class="py-4 px-6 font-bold text-slate-850 dark:text-slate-200">
                                                {{ res.simulacro_label }}
                                            </td>
                                            <td class="py-4 px-4 text-slate-500 dark:text-slate-400 font-medium">
                                                {{ res.tipo_examen_label }}
                                            </td>
                                            <td class="py-4 px-4 text-center whitespace-nowrap">
                                                <div class="flex items-center justify-center gap-1.5 font-bold">
                                                    <span class="text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20 px-2.5 py-1 rounded-lg text-[10px]">{{ res.total_buenas }} B</span>
                                                    <span class="text-rose-600 bg-rose-50 dark:bg-rose-950/20 px-2.5 py-1 rounded-lg text-[10px]">{{ res.total_malas }} M</span>
                                                    <span class="text-slate-550 bg-slate-100 dark:bg-slate-750 px-2.5 py-1 rounded-lg text-[10px]">{{ res.total_no_contesto }} N</span>
                                                </div>
                                            </td>
                                            <td class="py-4 px-4 text-right font-black text-slate-850 dark:text-white">
                                                {{ formatNumber(res.puntaje_total) }} pts
                                            </td>
                                            <td class="py-4 px-6 text-center whitespace-nowrap">
                                                <span 
                                                    class="px-2.5 py-1 rounded-xl text-[10px] font-extrabold uppercase tracking-wider inline-block"
                                                    :class="res.resultado === 1 
                                                        ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400' 
                                                        : 'bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-450'"
                                                >
                                                    {{ res.resultado === 1 ? 'Ingresó' : 'No Ingresó' }}
                                                </span>
                                            </td>
                                            <td class="py-4 px-6">
                                                <div class="flex flex-wrap gap-1.5 max-w-[400px]">
                                                    <div 
                                                        v-for="(eff, area) in res.efficiency_by_area" 
                                                        :key="area"
                                                        class="flex items-center gap-1 bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-750/50 rounded-lg px-2 py-0.5 text-[9px]"
                                                    >
                                                        <span class="font-bold text-slate-400 uppercase tracking-wide text-[8px]">{{ area }}:</span>
                                                        <span class="font-extrabold text-slate-700 dark:text-slate-350" :class="eff >= 70 ? 'text-emerald-600 dark:text-emerald-400' : eff >= 40 ? 'text-amber-500' : 'text-rose-500'">{{ formatNumber(eff) }}%</span>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr v-if="!reportData.resultados.length">
                                            <td colspan="6" class="py-8 text-center text-slate-400 italic">
                                                Este estudiante no tiene simulacros calificados en el sistema.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <!-- Empty state if no student selected -->
                    <div v-else-if="!loadingReport" class="bg-white dark:bg-slate-800 py-16 px-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm text-center flex flex-col items-center justify-center space-y-4">
                        <div class="p-4 rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-500">
                            <Icon icon="tabler:user-check" class="w-10 h-10 animate-pulse" />
                        </div>
                        <h4 class="text-md font-bold text-slate-800 dark:text-white">Ningún estudiante seleccionado</h4>
                        <p class="text-xs text-slate-400 max-w-sm">Por favor, seleccione un alumno en el menú superior para ver su historial de simulacros, eficiencia por área, diagnóstico y gráficos evolutivos.</p>
                    </div>
                </div>

                <!-- SUBTAB 4.2: REPORTES POR NIVEL / CUADRO DE MÉRITO -->
                <div v-else-if="reportSubTab === 'nivel'" class="space-y-6">
                    <!-- Filters Box -->
                    <div class="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm space-y-4">
                        <h3 class="text-md font-bold text-slate-800 dark:text-white flex items-center gap-2">
                            <Icon icon="tabler:school" class="w-5 h-5 text-indigo-500" />
                            Generar Cuadro de Mérito por Nivel y Bimestre
                        </h3>
                        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                            <div>
                                <label class="text-xs font-semibold text-slate-400 block mb-1.5">Subnivel / Grado</label>
                                <select 
                                    v-model="selectedReportLevelId" 
                                    @change="loadLevelReport"
                                    class="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-transparent py-2.5 px-4 focus:ring-1 focus:ring-indigo-500 text-slate-800 dark:text-slate-100"
                                >
                                    <option value="" class="dark:bg-slate-800 text-slate-400">Seleccione un grado...</option>
                                    <option v-for="lvl in childLevels" :key="lvl.id" :value="lvl.id" class="dark:bg-slate-800 text-slate-750 dark:text-slate-200">
                                        {{ lvl.label }}
                                    </option>
                                </select>
                            </div>
                            <div>
                                <label class="text-xs font-semibold text-slate-400 block mb-1.5">Bimestre Académico</label>
                                <select 
                                    v-model="selectedReportBimestre" 
                                    @change="loadLevelReport"
                                    class="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-transparent py-2.5 px-4 focus:ring-1 focus:ring-indigo-500 text-slate-800 dark:text-slate-100"
                                >
                                    <option value="" class="dark:bg-slate-800 text-slate-400">Seleccione un bimestre...</option>
                                    <option v-for="(label, key) in arrayBimestre" :key="key" :value="key" class="dark:bg-slate-800 text-slate-750 dark:text-slate-200">
                                        {{ label }}
                                    </option>
                                </select>
                            </div>
                            <div class="flex items-center h-[42px] pb-1">
                                <label class="flex items-center gap-2 cursor-pointer select-none">
                                    <input 
                                        type="checkbox" 
                                        v-model="showAllUsers" 
                                        @change="loadLevelReport"
                                        class="rounded border-slate-200 dark:border-slate-750 text-indigo-600 focus:ring-1 focus:ring-indigo-500 w-4 h-4 bg-transparent cursor-pointer transition-colors"
                                    />
                                    <span class="text-xs font-bold text-slate-600 dark:text-slate-350">Incluir Deudores</span>
                                </label>
                            </div>
                            <div class="flex items-center justify-end">
                                <button 
                                    @click="loadLevelReport"
                                    :disabled="loadingLevelReport || !selectedReportLevelId || !selectedReportBimestre"
                                    class="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold text-xs py-2.5 px-6 rounded-xl flex items-center justify-center gap-1.5 transition-all shadow-sm active:scale-95"
                                >
                                    <Icon v-if="loadingLevelReport" icon="tabler:loader" class="w-4 h-4 animate-spin" />
                                    <Icon v-else icon="tabler:refresh" class="w-4 h-4" />
                                    <span>Buscar y Ordenar</span>
                                </button>
                            </div>
                        </div>

                        <!-- Info / Print Suggestions Alert -->
                        <div class="bg-blue-50/70 dark:bg-indigo-950/20 border border-blue-100 dark:border-indigo-900/30 rounded-xl p-4 flex gap-3 text-xs text-blue-700 dark:text-blue-300 transition-all">
                            <Icon icon="tabler:info-circle" class="w-5 h-5 shrink-0 text-blue-500 dark:text-indigo-400 mt-0.5" />
                            <div class="space-y-1.5">
                                <p class="font-bold text-slate-800 dark:text-slate-200">Guía de Filtro e Impresión</p>
                                <ul class="list-disc pl-4 space-y-1 text-slate-600 dark:text-slate-350">
                                    <li>
                                        <b class="text-slate-750 dark:text-slate-250">Estado de Usuarios:</b> Por defecto (desmarcado), solo se muestran estudiantes que cuentan con un usuario en estado activo (No Deudores). Al marcar la casilla, se listarán estudiantes con usuario en cualquier estado (Deudores y No Deudores).
                                    </li>
                                    <li>
                                        <b class="text-slate-750 dark:text-slate-250">Sugerencia de Impresión de Diploma:</b> Para una correcta maquetación del diploma físico, en la ventana de impresión (<kbd class="px-1 py-0.5 bg-slate-100 dark:bg-slate-850 rounded border border-slate-200 dark:border-slate-750 text-[10px]">Ctrl + P</kbd>), ajuste la orientación a <b class="text-indigo-600 dark:text-indigo-400 font-extrabold">horizontal</b>, los márgenes a <b class="text-indigo-600 dark:text-indigo-400 font-extrabold">mínimos o ninguno</b> y active la opción de <b class="text-indigo-600 dark:text-indigo-400 font-extrabold">gráficos de fondo</b>.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <!-- Merit Table Box -->
                    <div v-if="levelReportData && levelReportData.length" class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm overflow-hidden">
                        <div class="p-6 border-b border-slate-100 dark:border-slate-700 flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h4 class="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <Icon icon="tabler:trophy" class="w-5 h-5 text-indigo-500" />
                                    Orden de Mérito y Cuadro de Puestos
                                </h4>
                                <p class="text-xs text-slate-400 mt-1">Lista ordenada descendentemente por puntaje total obtenido en simulacros del bimestre.</p>
                            </div>
                        </div>
                        
                        <div class="overflow-x-auto">
                            <table class="w-full text-left text-xs border-collapse">
                                <thead>
                                    <tr class="bg-slate-50 dark:bg-slate-900/60 text-slate-450 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-700">
                                        <th class="py-4 px-6 text-center w-16">Puesto</th>
                                        <th class="py-4 px-6">Estudiante</th>
                                        <th class="py-4 px-6">E.A.P.</th>
                                        <th class="py-4 px-4 text-center">Respuestas (B / M)</th>
                                        <th class="py-4 px-4 text-right">Puntaje Total</th>
                                        <th class="py-4 px-6 text-center">Resultado</th>
                                        <th class="py-4 px-6 text-center w-36">Acción</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-slate-100 dark:divide-slate-700/60">
                                    <tr v-for="res in levelReportData" :key="res.id" class="hover:bg-slate-50/40 dark:hover:bg-slate-900/20">
                                        <td class="py-4 px-6 text-center font-black">
                                            <span 
                                                class="w-8 h-8 rounded-full flex items-center justify-center text-xs shadow-sm mx-auto"
                                                :class="res.puesto === 1 
                                                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 font-black' 
                                                    : res.puesto === 2 
                                                    ? 'bg-slate-100 text-slate-800 dark:bg-slate-750 dark:text-slate-300' 
                                                    : res.puesto === 3 
                                                    ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400' 
                                                    : 'bg-transparent text-slate-500'"
                                            >
                                                {{ res.puesto }}º
                                            </span>
                                        </td>
                                        <td class="py-4 px-6 font-bold text-slate-850 dark:text-slate-200">
                                            {{ res.student_fullname }}
                                        </td>
                                        <td class="py-4 px-6 text-slate-500 dark:text-slate-400 font-medium">
                                            {{ res.label_eap || 'General' }}
                                        </td>
                                        <td class="py-4 px-4 text-center whitespace-nowrap">
                                            <div class="flex items-center justify-center gap-1.5 font-bold">
                                                <span class="text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20 px-2.5 py-1 rounded-lg text-[10px]">{{ res.total_buenas }} B</span>
                                                <span class="text-rose-600 bg-rose-50 dark:bg-rose-950/20 px-2.5 py-1 rounded-lg text-[10px]">{{ res.total_malas }} M</span>
                                            </div>
                                        </td>
                                        <td class="py-4 px-4 text-right font-black text-slate-850 dark:text-white text-sm">
                                            {{ formatNumber(res.puntaje_total) }} pts
                                        </td>
                                        <td class="py-4 px-6 text-center whitespace-nowrap">
                                            <span 
                                                class="px-2.5 py-1 rounded-xl text-[10px] font-extrabold uppercase tracking-wider inline-block"
                                                :class="res.resultado === 1 
                                                    ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400' 
                                                    : 'bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-450'"
                                            >
                                                {{ res.resultado === 1 ? 'Ingresó' : 'No Ingresó' }}
                                            </span>
                                        </td>
                                        <td class="py-3 px-4 text-center whitespace-nowrap">
                                            <div class="flex flex-col gap-1.5 items-center">
                                                <!-- Open for browser print -->
                                                <button 
                                                    @click="printDiploma(res)"
                                                    class="w-full bg-rose-50 hover:bg-rose-100 text-rose-600 dark:bg-rose-950/20 dark:hover:bg-rose-900/30 dark:text-rose-400 font-bold text-[10px] px-3 py-1.5 rounded-lg flex items-center justify-center gap-1.5 transition-all active:scale-95 shadow-sm border border-rose-100/50 dark:border-rose-950/50"
                                                    title="Abrir diploma para ver e imprimir desde el navegador"
                                                >
                                                    <Icon icon="tabler:certificate" class="w-3.5 h-3.5 text-rose-500" />
                                                    <span>Ver / Imprimir</span>
                                                </button>
                                                <!-- Download as PDF -->
                                                <!-- <button 
                                                    @click="downloadDiplomaPdf(res)"
                                                    class="w-full bg-indigo-50 hover:bg-indigo-100 text-indigo-600 dark:bg-indigo-950/20 dark:hover:bg-indigo-900/30 dark:text-indigo-400 font-bold text-[10px] px-3 py-1.5 rounded-lg flex items-center justify-center gap-1.5 transition-all active:scale-95 shadow-sm border border-indigo-100/50 dark:border-indigo-950/50"
                                                    title="Descargar diploma como archivo PDF"
                                                >
                                                    <Icon icon="tabler:file-type-pdf" class="w-3.5 h-3.5 text-indigo-500" />
                                                    <span>Descargar PDF</span>
                                                </button> -->
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Loading / Empty states -->
                    <div v-else-if="loadingLevelReport" class="bg-white dark:bg-slate-800 py-16 px-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm text-center flex flex-col items-center justify-center space-y-3">
                        <Icon icon="tabler:loader" class="w-10 h-10 text-indigo-500 animate-spin" />
                        <h4 class="text-md font-bold text-slate-800 dark:text-white">Buscando alumnos y calculando puestos...</h4>
                    </div>

                    <div v-else class="bg-white dark:bg-slate-800 py-16 px-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm text-center flex flex-col items-center justify-center space-y-4">
                        <div class="p-4 rounded-full bg-slate-50 dark:bg-slate-900/20 text-slate-450">
                            <Icon icon="tabler:trophy" class="w-10 h-10" />
                        </div>
                        <h4 class="text-md font-bold text-slate-800 dark:text-white">Sin datos para mostrar</h4>
                        <p class="text-xs text-slate-400 max-w-sm">Seleccione un grado y un bimestre académico con simulacros calificados para ver el cuadro de méritos y poder imprimir los diplomas de honor correspondientes.</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Reusable Modal Alert Component -->
        <ModalAlert 
            :show="alertState.show" 
            :title="alertState.title" 
            :message="alertState.message" 
            :type="alertState.type" 
            :confirmText="alertState.confirmText" 
            :cancelText="alertState.cancelText" 
            :showCancel="alertState.showCancel" 
            @close="alertState.onCancel ? alertState.onCancel() : alertState.show = false" 
            @confirm="alertState.onConfirm ? alertState.onConfirm() : alertState.show = false" 
        />

        <!-- Custom Toast Notification -->
        <Transition name="fade">
            <div 
                v-if="toastState.show" 
                class="fixed bottom-5 right-5 z-[100] bg-emerald-600 text-white font-bold text-xs py-3.5 px-6 rounded-xl shadow-2xl border border-emerald-500/30 flex items-center gap-3 transition-all duration-300 transform active:scale-95"
            >
                <Icon icon="tabler:circle-check" class="w-5 h-5 text-white shrink-0" />
                <span>{{ toastState.message }}</span>
            </div>
        </Transition>
        <!-- OMR Selection Modal / Bottom Sheet -->
        <Transition name="fade">
            <div v-if="showOMRSelector" class="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                <div 
                    class="bg-white dark:bg-slate-800 w-full max-w-sm rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700/80 p-6 space-y-6 overflow-hidden transform transition-all scale-100"
                    @click.stop
                >
                    <div class="text-center space-y-2">
                        <div class="mx-auto w-12 h-12 rounded-full bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                            <Icon icon="tabler:qrcode" class="w-6 h-6 animate-pulse" />
                        </div>
                        <h3 class="text-base font-bold text-slate-850 dark:text-white">Escaneo de Ficha (OMR)</h3>
                        <p class="text-xs text-slate-500 dark:text-slate-400">
                            Estudiante: <span class="font-bold text-slate-700 dark:text-slate-200">{{ currentOMRRow?.fullname }}</span>
                        </p>
                    </div>

                    <div class="grid grid-cols-1 gap-3">
                        <button 
                            @click="selectOMRCamera"
                            class="flex items-center gap-3 w-full p-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-xs shadow-md transition-all active:scale-[0.98] group text-left"
                        >
                            <div class="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center text-white shrink-0 group-hover:scale-110 transition-transform">
                                <Icon icon="tabler:camera" class="w-5 h-5" />
                            </div>
                            <div class="text-left">
                                <div class="font-bold text-xs text-white">Usar Cámara Posterior</div>
                                <div class="text-[9px] font-medium text-emerald-100">Tomar foto en vivo a la hoja física</div>
                            </div>
                        </button>

                        <button 
                            @click="selectOMRFile"
                            class="flex items-center gap-3 w-full p-4 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-650 text-slate-800 dark:text-white rounded-xl font-bold text-xs shadow-sm transition-all active:scale-[0.98] group text-left"
                        >
                            <div class="w-9 h-9 rounded-lg bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 shrink-0 group-hover:scale-110 transition-transform">
                                <Icon icon="tabler:photo" class="w-5 h-5" />
                            </div>
                            <div class="text-left">
                                <div class="font-bold text-xs text-slate-750 dark:text-white">Buscar en Galería / Archivos</div>
                                <div class="text-[9px] font-medium text-slate-400 dark:text-slate-350">Subir una foto tomada previamente</div>
                            </div>
                        </button>
                    </div>

                    <button 
                        @click="showOMRSelector = false"
                        class="w-full text-center py-2 text-xs font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                    >
                        Cancelar
                    </button>
                </div>
            </div>
        </Transition>
        <!-- Clave de Respuestas Modal -->
        <Transition name="fade">
            <div v-if="showClaveModal" class="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                <div class="bg-white dark:bg-slate-800 w-full max-w-4xl h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700/80">
                    
                    <!-- Header -->
                    <div class="p-5 border-b border-slate-150 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/80 flex items-center justify-between">
                        <div class="flex items-center gap-2.5">
                            <div class="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                                <Icon icon="tabler:key" class="w-5 h-5" />
                            </div>
                            <div>
                                <h3 class="text-sm font-bold text-slate-850 dark:text-white">Clave de Respuestas Correctas</h3>
                                <p class="text-[10px] text-slate-450 dark:text-slate-400">
                                    Simulacro: <span class="font-extrabold text-indigo-600 dark:text-indigo-400">{{ selectedSimulacro?.label }}</span>
                                </p>
                            </div>
                        </div>
                        <button 
                            @click="showClaveModal = false"
                            class="text-slate-400 hover:text-slate-655 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        >
                            <Icon icon="tabler:x" class="w-5 h-5" />
                        </button>
                    </div>

                    <!-- Scrollable Content -->
                    <div class="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/50 dark:bg-slate-900/20">
                        <div class="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-250 dark:border-amber-900/60 rounded-xl flex items-start gap-3">
                            <Icon icon="tabler:info-circle" class="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                            <div class="text-[10.5px] leading-relaxed text-amber-800 dark:text-amber-300 font-semibold">
                                Registre la clave oficial de respuestas correctas (A, B, C, D o E) para cada una de las preguntas de este simulacro. Esta plantilla será utilizada por el procesador de visión artificial OMR para calificar las fichas ópticas cargadas automáticamente. Las preguntas vacías no se tomarán en cuenta en la calificación.
                            </div>
                        </div>

                        <!-- Cursos Cards Group -->
                        <div v-for="(qs, cursoLabel) in groupCursosClave" :key="cursoLabel" class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-5 space-y-4 shadow-sm">
                            <div class="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-slate-750">
                                <h4 class="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                                    <Icon icon="tabler:books" class="w-4 h-4 text-emerald-500" />
                                    {{ cursoLabel }}
                                </h4>
                                <span class="bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 font-bold text-[9px] px-2 py-0.5 rounded-full uppercase">
                                    {{ qs.length }} Preguntas
                                </span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                <div 
                                    v-for="q in qs" 
                                    :key="q.numero_pregunta"
                                    class="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-750/70 rounded-xl hover:shadow-sm transition-all duration-200"
                                >
                                    <span class="text-xs font-black text-slate-700 dark:text-slate-200 w-10 shrink-0 text-center">
                                        P. {{ q.numero_pregunta < 10 ? '0' + q.numero_pregunta : q.numero_pregunta }}
                                    </span>
                                    <div class="flex items-center gap-1">
                                        <button 
                                            v-for="letter in ['A', 'B', 'C', 'D', 'E']" 
                                            :key="letter"
                                            @click="q.respuesta_correcta = q.respuesta_correcta === letter ? null : letter"
                                            class="w-7 h-7 rounded-full text-xs font-extrabold flex items-center justify-center transition-all select-none"
                                            :class="q.respuesta_correcta === letter 
                                                ? 'bg-indigo-600 text-white shadow-sm font-black scale-105' 
                                                : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-750 text-slate-650 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/60'"
                                        >
                                            {{ letter }}
                                        </button>
                                        <button 
                                            v-if="q.respuesta_correcta"
                                            @click="q.respuesta_correcta = null"
                                            class="text-slate-400 hover:text-rose-500 p-1.5 rounded transition-colors shrink-0"
                                            title="Borrar respuesta"
                                        >
                                            <Icon icon="tabler:eraser" class="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Footer -->
                    <div class="p-5 border-t border-slate-150 dark:border-slate-750 bg-slate-50/80 dark:bg-slate-800/80 flex items-center justify-end gap-3 shrink-0">
                        <button 
                            @click="showClaveModal = false"
                            class="border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-650 dark:text-slate-200 font-bold text-xs py-2 px-4 rounded-xl transition-all"
                        >
                            Cancelar
                        </button>
                        <button 
                            @click="saveClave"
                            :disabled="savingClave"
                            class="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs py-2.5 px-5 rounded-xl flex items-center gap-1.5 transition-all shadow-md active:scale-95 disabled:opacity-50"
                        >
                            <Icon v-if="savingClave" icon="tabler:loader" class="w-4 h-4 animate-spin" />
                            <Icon v-else icon="tabler:device-floppy" class="w-4 h-4" />
                            <span>{{ savingClave ? 'Guardando...' : 'Guardar Clave' }}</span>
                        </button>
                    </div>

                </div>
            </div>
        </Transition>
        <!-- OMR File Selector (Gallery / File) -->
        <input 
            type="file" 
            ref="fileInputOMRFile" 
            class="hidden" 
            accept="image/*" 
            @change="handleOMRFileChange"
        />
        <!-- OMR Camera Input (Forced to Rear Camera) -->
        <input 
            type="file" 
            ref="fileInputOMRCamera" 
            class="hidden" 
            accept="image/*" 
            capture="environment" 
            @change="handleOMRFileChange"
        />
    </BreezeAuthenticatedLayout>
</template>

<style>
@media print {
    /* Hide everything by default */
    body * {
        visibility: hidden;
    }
    /* Show ONLY the print-report-section and its descendants */
    #print-report-section, #print-report-section * {
        visibility: visible;
    }
    #print-report-section {
        position: absolute;
        left: 0;
        top: 0;
        width: 100% !important;
        background: white !important;
        color: black !important;
        box-shadow: none !important;
        border: none !important;
        padding: 0 !important;
        margin: 0 !important;
    }
    
    /* Normalize bg colors for physical printer */
    .bg-white, .bg-slate-50, .bg-gradient-to-br {
        background-color: #fff !important;
        background: #fff !important;
        border: 1px solid #e2e8f0 !important;
    }
    
    /* Prevent cut in half charts / rows */
    .print-break-inside-avoid {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
    }
    
    .overflow-x-auto {
        overflow: visible !important;
    }
}
</style>
